// src/pages/PrintScreen.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { downloadPDF } from "../utils/exportHelpers";

/* ===== Helpers ===== */
const isTakeawayName = (name) =>
  typeof name === "string" && name.toLowerCase().startsWith("takeaway");
const extractTakeawayIndex = (name) => {
  if (!name) return null;
  const lower = String(name).toLowerCase().trim();
  if (lower === "takeaway") return 1;
  const m = lower.match(/takeaway[\s-]*(\d+)/);
  if (!m) return null;
  const n = parseInt(m[1], 10);
  return Number.isNaN(n) ? null : n;
};
const formatTakeawayLabel = (name) => {
  if (!name) return "TakeAway";
  const idx = extractTakeawayIndex(name);
  return idx ? `TakeAway ${idx}` : String(name);
};
const buildTableMetaMap = (list = []) => {
  const map = {};
  if (!Array.isArray(list)) return map;
  list.forEach((entry) => {
    const key =
      entry?.table_key ??
      entry?.tableKey ??
      entry?.key ??
      entry?.name ??
      entry?.table ??
      entry?.tableid ??
      entry?.tableId ??
      entry?.id;
    const displayName =
      entry?.display_name ??
      entry?.displayName ??
      entry?.name ??
      entry?.table ??
      entry?.table_key ??
      entry?.tableKey ??
      entry?.tableid ??
      entry?.tableId ??
      entry?.id ??
      "";
    const typeRaw =
      entry?.table_type ?? entry?.tableType ?? entry?.tabletype ?? entry?.type;
    const type = String(typeRaw || "").toLowerCase() === "room" ? "room" : "table";
    const meta = { displayName, type };
    const addKey = (k) => {
      if (k === null || k === undefined || k === "") return;
      map[String(k)] = meta;
    };
    addKey(key);
    addKey(entry?.id);
    addKey(entry?.tableid);
    addKey(entry?.tableId);
    addKey(entry?.table_id);
  });
  return map;
};

const N = (v) => {
  const x = parseFloat(String(v ?? "0").replace(/[, ]+/g, ""));
  return Number.isFinite(x) ? x : 0;
};
const money = (v) => N(v).toFixed(2);
const parseAddonIds = (raw) => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw.map((v) => Number(v)).filter(Boolean);
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed))
        return parsed.map((v) => Number(v)).filter(Boolean);
    } catch {
      // fall through
    }
    return raw
      .split(/[, ]+/)
      .map((v) => Number(v))
      .filter(Boolean);
  }
  return [];
};
const buildLineKey = (productId, variantId, addonIds = []) => {
  const addonKey =
    Array.isArray(addonIds) && addonIds.length
      ? addonIds.slice().sort((a, b) => a - b).join("_")
      : "na";
  const variantKey = variantId ? `v${variantId}` : "base";
  return `p${productId}-${variantKey}-a${addonKey}`;
};
const parseMaybeJson = (value) => {
  if (typeof value !== "string") return value;
  const trimmed = value.trim();
  if (!trimmed) return value;
  if (
    (trimmed.startsWith("[") && trimmed.endsWith("]")) ||
    (trimmed.startsWith("{") && trimmed.endsWith("}"))
  ) {
    try {
      return JSON.parse(trimmed);
    } catch {
      return value;
    }
  }
  return value;
};
const normalizeRows = (value) => {
  const parsed = parseMaybeJson(value);
  if (Array.isArray(parsed)) return parsed.flatMap((entry) => normalizeRows(entry));
  if (parsed && typeof parsed === "object") return [parsed];
  return [];
};
const normalizeTaxDetailRows = (taxDetails) => {
  const lines = Array.isArray(taxDetails?.lines) ? taxDetails.lines : [];
  return lines
    .map((line) => {
      const components = Array.isArray(line?.components) ? line.components : [];
      const findComponentAmount = (needle) => {
        const match = components.find((component) =>
          String(component?.name || "")
            .toLowerCase()
            .includes(needle)
        );
        return N(match?.amount);
      };

      const taxableamount = N(
        line?.base ?? line?.taxable ?? line?.taxableamount ?? line?.taxableAmount
      );
      const rate = N(
        line?.rate ?? line?.taxRate ?? line?.percent ?? line?.percentage
      );
      const amount = N(line?.amount ?? line?.tax_amount ?? 0);
      const cgst = findComponentAmount("cgst");
      const sgst = findComponentAmount("sgst");
      const igst = cgst === 0 && sgst === 0 ? amount : 0;

      return {
        taxableamount,
        rate,
        cgst,
        sgst,
        igst,
        total: N(line?.total ?? amount ?? (igst > 0 ? igst : cgst + sgst)),
      };
    })
    .filter((row) => row.taxableamount > 0 || row.total > 0);
};
const normalizePrintableTaxBreakup = (rows, taxLabel = "GST") => {
  const normalizedRows = normalizeRows(rows)
    .map((row) => {
      const taxable = N(
        row?.taxableamount ?? row?.taxable ?? row?.taxableAmount
      );
      const rate = N(
        row?.rate ?? row?.taxRate ?? row?.percent ?? row?.percentage
      );
      const cgst = N(row?.cgst ?? row?.cgstAmount ?? row?.cgstvalue);
      const sgst = N(row?.sgst ?? row?.sgstAmount ?? row?.sgstvalue);
      const igst = N(row?.igst ?? row?.igstAmount);
      const total = N(
        row?.total ??
          row?.totalamount ??
          row?.totalGST ??
          row?.amount ??
          row?.tax_amount
      );

      return {
        taxable,
        rate,
        cgst,
        sgst,
        igst,
        total: igst > 0 ? igst : total || cgst + sgst,
      };
    })
    .filter((row) => row.total > 0);

  const splitTaxBreakup =
    String(taxLabel || "GST")
      .trim()
      .toUpperCase() === "GST" ||
    normalizedRows.some((row) => row.cgst > 0 || row.sgst > 0);

  return {
    splitTaxBreakup,
    rows: normalizedRows.map((row) =>
      splitTaxBreakup ? row : { ...row, cgst: 0, sgst: 0, total: row.igst || row.total }
    ),
  };
};

/* ===== Thermal (BLE) helpers ===== */
const ESC = 0x1b;
const GS = 0x1d;
const INIT_CMD = new Uint8Array([ESC, 0x40]);
const CUT_CMD = new Uint8Array([GS, 0x56, 0x00]);
const ALIGN_LEFT = new Uint8Array([ESC, 0x61, 0x00]);
const ALIGN_CENTER = new Uint8Array([ESC, 0x61, 0x01]);
const SIZE_ON = "\x1b\x21\x30";
const SIZE_OFF = "\x1b\x21\x00";
const BLE_LINE_WIDTH = 32;
const NAME_W = 14;
const QTY_W = 3;
const RATE_W = 6;
const AMT_W = 7;
const PRINT_PROFILES = {
  "2in": {
    lineWidth: 32,
    nameW: 14,
    qtyW: 3,
    rateW: 6,
    amtW: 7,
    logoWidth: 140,
    qrSize: 220,
    taxCols: { taxable: 8, cgst: 7, sgst: 7, total: 9 },
  },
  "3in": {
    lineWidth: 48,
    nameW: 24,
    qtyW: 4,
    rateW: 8,
    amtW: 11,
    logoWidth: 170,
    qrSize: 280,
    taxCols: { taxable: 12, cgst: 12, sgst: 12, total: 12 },
  },
  "4in": {
    lineWidth: 64,
    nameW: 32,
    qtyW: 5,
    rateW: 10,
    amtW: 13,
    logoWidth: 200,
    qrSize: 320,
    taxCols: { taxable: 16, cgst: 16, sgst: 16, total: 16 },
  },
  a4: {
    lineWidth: 72,
    nameW: 38,
    qtyW: 6,
    rateW: 12,
    amtW: 15,
    logoWidth: 220,
    qrSize: 340,
    taxCols: { taxable: 18, cgst: 18, sgst: 18, total: 18 },
  },
};
const normalizePrinterWidthPreference = (value) => {
  const raw = String(value || "").trim().toLowerCase();
  if (!raw) return null;
  if (raw === "a4" || raw === "a4-size" || raw === "a4size") {
    return "a4";
  }
  if (
    raw === "4" ||
    raw === "4in" ||
    raw === "4inch" ||
    raw === "4-inch" ||
    raw === "4 in" ||
    raw === "112mm" ||
    raw === "104mm" ||
    raw === "102mm" ||
    raw === "100mm"
  ) {
    return "4in";
  }
  if (
    raw === "3" ||
    raw === "3in" ||
    raw === "3inch" ||
    raw === "3-inch" ||
    raw === "80mm" ||
    raw === "76mm" ||
    raw === "72mm" ||
    raw === "80"
  ) {
    return "3in";
  }
  if (
    raw === "2" ||
    raw === "2in" ||
    raw === "2inch" ||
    raw === "2-inch" ||
    raw === "58mm" ||
    raw === "58"
  ) {
    return "2in";
  }
  return null;
};
const getPaperMeta = (widthKey) => {
  const normalized = normalizePrinterWidthPreference(widthKey) || "2in";
  if (normalized === "a4") {
    return {
      key: "a4",
      screenWidth: "960px",
      printWidth: "210mm",
      printSize: "A4",
      printMargin: "10mm",
    };
  }
  if (normalized === "4in") {
    return {
      key: "4in",
      screenWidth: "860px",
      printWidth: "104mm",
      printSize: "104mm",
      printMargin: "4mm",
    };
  }
  if (normalized === "3in") {
    return {
      key: "3in",
      screenWidth: "820px",
      printWidth: "80mm",
      printSize: "80mm",
      printMargin: "4mm",
    };
  }
  return {
    key: "2in",
    screenWidth: "780px",
    printWidth: "58mm",
    printSize: "58mm",
    printMargin: "4mm",
  };
};
const readStorageBool = (key, fallback) => {
  const raw = localStorage.getItem(key);
  if (raw === null) return fallback;
  return raw === "true";
};
const readStorageString = (key, fallback = "") =>
  (localStorage.getItem(key) || fallback || "").toString();
const sizeToQrPixels = (size, fallback = 220) => {
  if (size === "small") return 170;
  if (size === "large") return 280;
  return fallback;
};
const sizeToLogoPixels = (size, fallback = 320) => {
  if (size === "small") return 110;
  if (size === "large") return 170;
  return Math.min(Number(fallback) || 140, 140);
};
const sizeToTextRem = (size, fallback = 1) => {
  if (size === "small") return Math.max(0.92, fallback - 0.08);
  if (size === "large") return fallback + 0.14;
  return fallback;
};
const chunkBytes = (bytes, size = 20) => {
  const out = [];
  for (let i = 0; i < bytes.length; i += size) out.push(bytes.slice(i, i + size));
  return out;
};
const centerLine = (text = "", width = BLE_LINE_WIDTH) => {
  const s = String(text || "").trim();
  if (!s) return "";
  if (s.length >= width) return s.slice(0, width);
  const gap = width - s.length;
  const left = Math.floor(gap / 2);
  return " ".repeat(left) + s + " ".repeat(gap - left);
};
const padR = (txt, w) => {
  const s = String(txt ?? "");
  return (s + " ".repeat(Math.max(0, w - s.length))).slice(0, w);
};
const padL = (txt, w) => {
  const s = String(txt ?? "");
  return (" ".repeat(Math.max(0, w - s.length)) + s).slice(-w);
};
const wrapText = (text, width) => {
  const s = String(text || "-");
  const out = [];
  let remaining = s.trim();
  while (remaining.length) {
    if (remaining.length <= width) {
      out.push(remaining);
      break;
    }
    const slice = remaining.slice(0, width + 1);
    const lastSpace = slice.lastIndexOf(" ");
    if (lastSpace > 0) {
      out.push(slice.slice(0, lastSpace));
      remaining = remaining.slice(lastSpace + 1);
    } else {
      out.push(remaining.slice(0, width));
      remaining = remaining.slice(width);
    }
    remaining = remaining.trimStart();
  }
  return out.length ? out : ["-"];
};
const col4 = (name, qty, rate, amt, widths = {}) => {
  const nameW = widths.nameW ?? NAME_W;
  const qtyW = widths.qtyW ?? QTY_W;
  const rateW = widths.rateW ?? RATE_W;
  const amtW = widths.amtW ?? AMT_W;
  return (
    padR(String(name ?? ""), nameW) +
    padL(String(qty ?? ""), qtyW) +
    " " +
    padL(String(rate ?? ""), rateW) +
    padL(String(amt ?? ""), amtW)
  );
};
const padLine = (label, right, width = BLE_LINE_WIDTH) => {
  const rightStr = String(right || "");
  const maxLabel = Math.max(1, width - rightStr.length - 1);
  const safeLabel = (label || "").toString().slice(0, maxLabel);
  const spaces = Math.max(1, width - safeLabel.length - rightStr.length);
  return `${safeLabel}${" ".repeat(spaces)}${rightStr}`;
};
const pairLine = (
  leftLabel,
  leftValue,
  rightLabel,
  rightValue,
  width = BLE_LINE_WIDTH
) => {
  const half = Math.max(12, Math.floor(width / 2));
  const leftText = `${leftLabel}: ${String(leftValue || "-")}`;
  const rightText = `${rightLabel}: ${String(rightValue || "-")}`;
  return padR(leftText, half) + padR(rightText, width - half);
};
const buildSizedCenteredLines = (text, width, sizeKey) => {
  const trimmed = (text || "").toString().trim();
  if (!trimmed) return [];
  const isLarge = sizeKey === "large";
  const lineWidth = isLarge ? Math.max(1, Math.floor(width / 2)) : width;
  const lines = wrapText(trimmed, lineWidth);
  if (!isLarge) return lines.map((line) => centerLine(line, width));
  return lines.map((line) => `${SIZE_ON}${centerLine(line, width)}${SIZE_OFF}`);
};
const canvasFromImage = async (src, maxWidth = 384, timeoutMs = 2200) => {
  if (!src) return null;
  return new Promise((resolve, reject) => {
    const img = new Image();
    const timer = setTimeout(() => {
      reject(new Error("Image load timeout"));
    }, timeoutMs);
    img.crossOrigin = "anonymous";
    img.onload = () => {
      clearTimeout(timer);
      const ratio = img.width ? Math.min(1, maxWidth / img.width) : 1;
      const w = Math.max(1, Math.round(img.width * ratio));
      const h = Math.max(1, Math.round(img.height * ratio));
      const canvas = document.createElement("canvas");
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, w, h);
      resolve(canvas);
    };
    img.onerror = (e) => {
      clearTimeout(timer);
      reject(e);
    };
    img.src = src;
  });
};
const canvasToEscPosRaster = (canvas) => {
  if (!canvas) return null;
  const ctx = canvas.getContext("2d");
  const { width, height } = canvas;
  const imageData = ctx.getImageData(0, 0, width, height).data;
  const bytesPerRow = Math.ceil(width / 8);
  const totalBytes = bytesPerRow * height;
  const raster = new Uint8Array(8 + totalBytes);
  raster[0] = 0x1d;
  raster[1] = 0x76;
  raster[2] = 0x30;
  raster[3] = 0x00;
  raster[4] = bytesPerRow & 0xff;
  raster[5] = (bytesPerRow >> 8) & 0xff;
  raster[6] = height & 0xff;
  raster[7] = (height >> 8) & 0xff;
  for (let y = 0; y < height; y++) {
    for (let xByte = 0; xByte < bytesPerRow; xByte++) {
      let byte = 0;
      for (let bit = 0; bit < 8; bit++) {
        const x = xByte * 8 + bit;
        if (x >= width) continue;
        const idx = (y * width + x) * 4;
        const r = imageData[idx];
        const g = imageData[idx + 1];
        const b = imageData[idx + 2];
        const lum = 0.299 * r + 0.587 * g + 0.114 * b;
        if (lum < 160) byte |= 1 << (7 - bit);
      }
      raster[8 + y * bytesPerRow + xByte] = byte;
    }
  }
  return raster;
};
const concatUint8 = (...parts) => {
  const total = parts.reduce((sum, part) => sum + (part?.length || 0), 0);
  const out = new Uint8Array(total);
  let offset = 0;
  parts.forEach((part) => {
    if (!part?.length) return;
    out.set(part, offset);
    offset += part.length;
  });
  return out;
};
const buildEscPosQrCommands = (text, opts = {}) => {
  const payload = String(text || "").trim();
  if (!payload) return [];
  const model = Number(opts?.model) === 1 ? 49 : 50;
  const moduleSize = Math.max(3, Math.min(8, Number(opts?.moduleSize) || 6));
  const ecc = Math.max(48, Math.min(51, Number(opts?.ecc) || 48));
  const data = new TextEncoder().encode(payload);
  const len = data.length + 3;
  const pL = len & 0xff;
  const pH = (len >> 8) & 0xff;
  const storePrefix = new Uint8Array([GS, 0x28, 0x6b, pL, pH, 0x31, 0x50, 0x30]);
  return [
    new Uint8Array([GS, 0x28, 0x6b, 0x04, 0x00, 0x31, 0x41, model, 0x00]),
    new Uint8Array([GS, 0x28, 0x6b, 0x03, 0x00, 0x31, 0x43, moduleSize]),
    new Uint8Array([GS, 0x28, 0x6b, 0x03, 0x00, 0x31, 0x45, ecc]),
    concatUint8(storePrefix, data),
    new Uint8Array([GS, 0x28, 0x6b, 0x03, 0x00, 0x31, 0x51, 0x30]),
  ];
};
const normalizeLogoCandidates = (url) => {
  if (!url) return [];
  let primary = url;
  if (!/^https?:\/\//i.test(primary)) {
    primary = `https://qr.billsarthi.com/public/${primary.replace(/^\/?/, "")}`;
  }
  const candidates = [primary];
  try {
    const stripped = primary.replace(/^https?:\/\//i, "");
    candidates.push(`https://images.weserv.nl/?url=${encodeURIComponent(stripped)}`);
  } catch {
    // ignore
  }
  return candidates;
};
const logoRasterCache = new Map();
const logoRasterFromUrl = async (url, size = 140) => {
  const input = String(url || "").trim();
  if (!input) return null;
  const safeSize = Number(size) > 0 ? Number(size) : 140;
  const key = `${input}::${safeSize}`;
  if (logoRasterCache.has(key)) return logoRasterCache.get(key);
  const choices = normalizeLogoCandidates(input);
  for (const candidate of choices) {
    try {
      const logoCanvas = await canvasFromImage(candidate, safeSize, 1400);
      const logoCmd = canvasToEscPosRaster(logoCanvas);
      if (logoCmd) {
        logoRasterCache.set(key, logoCmd);
        return logoCmd;
      }
    } catch {
      // try next candidate
    }
  }
  return null;
};

const extractGSTDetails = (details, breakup, items = []) => {
  const detailRows = normalizeRows(details);
  const totalGST = detailRows.reduce(
    (sum, row) =>
      sum +
      N(
        row?.gst ??
          row?.totalGST ??
          row?.total_gst ??
          row?.gst_total ??
          row?.tax
      ),
    0
  );
  const rows = normalizeRows(breakup);
  if (!totalGST && rows.length) {
    const sum = rows.reduce((acc, r) => {
      const cgst = N(r?.cgst ?? r?.cgstAmount);
      const sgst = N(r?.sgst ?? r?.sgstAmount);
      const igst = N(r?.igst ?? r?.igstAmount);
      const row = igst > 0 ? igst : cgst + sgst;
      return acc + row;
    }, 0);
    return { totalGST: sum, rows };
  }
  if (!totalGST && Array.isArray(items) && items.length) {
    const sum = items.reduce((acc, item) => {
      const qty = N(item?.qty ?? item?.quantity ?? item?.qtyy ?? 0);
      const tax = N(
        item?.taxamount ??
          item?.tax_amount ??
          N(item?.cgst ?? item?.cgstAmount ?? 0) +
            N(item?.sgst ?? item?.sgstAmount ?? 0) +
            N(item?.igst ?? item?.igstAmount ?? 0)
      );
      return acc + qty * tax;
    }, 0);
    return { totalGST: sum, rows };
  }
  return { totalGST, rows };
};

const computeDiscount = ({
  total,
  discount,
  discount_type,
  discount_amount,
  discount_percent,
  gstTotal,
}) => {
  let pct = 0,
    flat = 0;
  if (N(discount_percent) > 0) pct = N(discount_percent);
  else if ((discount_type || "").toLowerCase() === "percentage")
    pct = N(discount);
  else if (N(discount_amount) > 0) flat = N(discount_amount);
  else {
    const d = N(discount);
    if (d > 0 && d <= 100) pct = d;
    else flat = d;
  }
  const amtFromPct = pct > 0 ? (N(total) * pct) / 100 : 0;
  const amount = flat > 0 ? flat : amtFromPct;
  const taxable = Math.max(0, N(total) - N(gstTotal));
  return {
    percent: pct,
    amount: Number(amount.toFixed(2)),
    taxable: Number(taxable.toFixed(2)),
  };
};

const formatDateTime = (raw) => {
  if (!raw) return "-";
  const d = new Date(raw);
  if (isNaN(d.getTime())) return String(raw);
  const pad = (v) => String(v).padStart(2, "0");
  const dd = pad(d.getDate());
  const mm = pad(d.getMonth() + 1);
  const yyyy = d.getFullYear();
  const hh = pad(d.getHours());
  const min = pad(d.getMinutes());
  return `${dd}-${mm}-${yyyy} ${hh}:${min}`;
};

const API_BASE = "https://qr.billsarthi.in/public/api";
const SUPPORT_WHATSAPP = "919988777409";
const DEFAULT_RECHARGE_AMOUNTS = [499, 999, 1999];
const digitsOnly = (value) => String(value || "").replace(/\D+/g, "");
const formatDateInputValue = (raw) => {
  const date = raw ? new Date(raw) : new Date();
  if (Number.isNaN(date.getTime())) return "";
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};
const formatTimeInputValue = (raw) => {
  const date = raw ? new Date(raw) : new Date();
  if (Number.isNaN(date.getTime())) return "";
  const hh = String(date.getHours()).padStart(2, "0");
  const mm = String(date.getMinutes()).padStart(2, "0");
  return `${hh}:${mm}`;
};
const mergeDateAndTime = (dateValue, timeValue) => {
  if (!dateValue) return null;
  const safeTime = timeValue || "00:00";
  const merged = new Date(`${dateValue}T${safeTime}:00`);
  return Number.isNaN(merged.getTime()) ? null : merged;
};
const formatBillDateForApi = (date) => {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};
const formatBillTimeForApi = (date) => {
  const hh = String(date.getHours()).padStart(2, "0");
  const mm = String(date.getMinutes()).padStart(2, "0");
  const ss = String(date.getSeconds()).padStart(2, "0");
  return `${hh}:${mm}:${ss}`;
};
const openSupportWhatsApp = (message) => {
  const url = `https://wa.me/${SUPPORT_WHATSAPP}?text=${encodeURIComponent(
    String(message || "").trim()
  )}`;
  window.open(url, "_blank", "noopener,noreferrer");
};

const PrintScreen = () => {
  const navigate = useNavigate();
  const { orderId } = useParams();
  const { state } = useLocation();

  const initialOrder = state?.order || null;
  const userData = useMemo(() => {
    try {
      const raw = localStorage.getItem("userData");
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return parsed?.data || parsed || null;
    } catch {
      return null;
    }
  }, []);

  const [order, setOrder] = useState(initialOrder);
  const [orderDetailResponse, setOrderDetailResponse] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(!initialOrder);
  const [error, setError] = useState("");
  const [variantMap, setVariantMap] = useState({});
  const [variantCountByProduct, setVariantCountByProduct] = useState({});
  const [addonsById, setAddonsById] = useState({});
  const [tableMetaMap, setTableMetaMap] = useState({});
  const [blePrinting, setBlePrinting] = useState(false);
  const [bleStatus, setBleStatus] = useState("");
  const [printerWidthSetting, setPrinterWidthSetting] = useState("2in");
  const [showTaxBreakupOnBillSetting, setShowTaxBreakupOnBillSetting] = useState(true);
  const [restaurantNameSizeSetting, setRestaurantNameSizeSetting] = useState("small");
  const [customerNameSizeSetting, setCustomerNameSizeSetting] = useState("small");
  const [showPaymentModeOnBillSetting, setShowPaymentModeOnBillSetting] = useState(true);
  const [showLogoOnBillSetting, setShowLogoOnBillSetting] = useState(true);
  const [showQrOnBillSetting, setShowQrOnBillSetting] = useState(true);
  const [alwaysPrintUpiQrSetting, setAlwaysPrintUpiQrSetting] = useState(false);
  const [printTokenNoSetting, setPrintTokenNoSetting] = useState(true);
  const [printGoogleReviewQrSetting, setPrintGoogleReviewQrSetting] =
    useState(false);
  const [logoSizeSetting, setLogoSizeSetting] = useState("medium");
  const [upiQrSizeSetting, setUpiQrSizeSetting] = useState("large");
  const [googleReviewQrSizeSetting, setGoogleReviewQrSizeSetting] =
    useState("small");
  const [gstPrintFormatSetting, setGstPrintFormatSetting] = useState("both");
  const [businessNameSizeSetting, setBusinessNameSizeSetting] =
    useState("medium");
  const [totalAmountSizeSetting, setTotalAmountSizeSetting] = useState("medium");
  const [savingAmountSizeSetting, setSavingAmountSizeSetting] =
    useState("medium");
  const [showPhoneOnBillSetting, setShowPhoneOnBillSetting] = useState(true);
  const [showThanksOnBillSetting, setShowThanksOnBillSetting] = useState(true);
  const [logoModalOpen, setLogoModalOpen] = useState(false);
  const [upiModalOpen, setUpiModalOpen] = useState(false);
  const [billMetaModalOpen, setBillMetaModalOpen] = useState(false);
  const [whatsAppModalOpen, setWhatsAppModalOpen] = useState(false);
  const [pickedLogoFile, setPickedLogoFile] = useState(null);
  const [pickedLogoPreview, setPickedLogoPreview] = useState("");
  const [logoUploading, setLogoUploading] = useState(false);
  const [upiValue, setUpiValue] = useState("");
  const [upiSaving, setUpiSaving] = useState(false);
  const [editBillNo, setEditBillNo] = useState("");
  const [editBillDate, setEditBillDate] = useState("");
  const [editBillTime, setEditBillTime] = useState("");
  const [savingBillMeta, setSavingBillMeta] = useState(false);
  const [whatsAppNumber, setWhatsAppNumber] = useState("");
  const [sharingInvoice, setSharingInvoice] = useState(false);
  const [qrEntitlement, setQrEntitlement] = useState(null);
  const [rechargeAmount, setRechargeAmount] = useState(999);
  const printerCharRef = useRef(null);
  const receiptRef = useRef(null);
  const lastBleDeviceIdRef = useRef(
    localStorage.getItem("ble_last_device_id") ||
      localStorage.getItem("printerConnectedId") ||
      ""
  );

  const [restInfo, setRestInfo] = useState({
    restaurant_name: "Bill Sarthi",
    addressline1: "",
    addressline2: "",
    addressline3: "",
    mobile_number: "",
    gstno: "",
    upiaddress: "",
    avatar_url: "",
  });

  /* ===== Restaurant info from localStorage (mobile style) ===== */
  useEffect(() => {
    try {
      const stored = localStorage.getItem("userData");
      if (!stored) return;
      const parsed = JSON.parse(stored);
      const u = parsed?.data || parsed || {};

      const info = {
        restaurant_name:
          u.restaurant_name || u.restname || u.name || "Bill Sarthi",
        addressline1: u.addressline1 || u.address || "",
        addressline2: u.addressline2 || "",
        addressline3: u.addressline3 || "",
        mobile_number:
          u.mobile_number || u.phonenumber || u.phone || u.contact_number || "",
        gstno: u.gstno || u.gstin || u.gstnumber || "",
        upiaddress: u.upiaddress || u.upiid || "",
        google_review_url:
          u.googlereviewurl || u.google_review_url || u.googleReviewUrl || "",
        avatar_url:
          u.logo ||
          u.restaurant_logo ||
          u.avatar_url ||
          u.photo ||
          "",
      };

      setRestInfo(info);
      setUpiValue((info.upiaddress || "").trim());
    } catch (e) {
      console.log("userData parse error", e);
    }
  }, []);
  const resolvedRestaurantId = useMemo(
    () =>
      Number(
        userData?.id ||
          order?.restaurantid ||
          initialOrder?.restaurantid ||
          orderDetailResponse?.orderdetails?.restaurantid ||
          0
      ) || 0,
    [userData, order, initialOrder, orderDetailResponse]
  );
  const customFieldRows = useMemo(() => {
    const values = [
      (userData?.customfield1 || "").toString().trim(),
      (userData?.customfield2 || "").toString().trim(),
      (userData?.customfield3 || "").toString().trim(),
    ].filter(Boolean);
    return values.map((value, index) => ({
      label: `Custom Field ${index + 1}`,
      value,
    }));
  }, [userData]);

  useEffect(() => {
    const printerWidthKey = "printerWidth";
    const printerWidthLegacyKey = "printer_width";
    const taxBreakupKey = "pos_show_tax_breakup_on_bill";
    const restaurantNameSizeKey = "pos_restaurant_name_size";
    const customerNameSizeKey = "pos_customer_name_size";
    const paymentModeOnBillKey = "pos_show_payment_mode_on_bill";
    const printLogoKey = "printer_setting_printLogoOnBill";
    const printUpiQrKey = "printer_setting_printUpiQrOnBill";
    const alwaysPrintUpiQrKey = "printer_setting_alwaysPrintUpiQr";
    const printTokenKey = "printer_setting_printTokenNo";
    const printReviewQrKey = "printer_setting_printGoogleReviewQr";
    const logoSizeKey = "printer_setting_logoSize";
    const upiQrSizeKey = "printer_setting_upiQrSize";
    const reviewQrSizeKey = "printer_setting_googleReviewQrSize";
    const gstFormatKey = "printer_setting_gstPrintFormat";
    const businessNameSizeKey = "printer_setting_businessNameSize";
    const totalAmountSizeKey = "printer_setting_totalAmountSize";
    const savingAmountSizeKey = "printer_setting_savingAmountSize";

    const savedPrinterWidth =
      localStorage.getItem(printerWidthKey) ||
      localStorage.getItem(printerWidthLegacyKey);
    const normalizedWidth = normalizePrinterWidthPreference(savedPrinterWidth);
    if (normalizedWidth) {
      setPrinterWidthSetting(normalizedWidth);
    }
    const savedTaxBreakup = localStorage.getItem(taxBreakupKey);
    if (savedTaxBreakup !== null) {
      setShowTaxBreakupOnBillSetting(savedTaxBreakup === "true");
    }
    const savedRestaurantNameSize = localStorage.getItem(restaurantNameSizeKey);
    if (savedRestaurantNameSize === "large" || savedRestaurantNameSize === "small") {
      setRestaurantNameSizeSetting(savedRestaurantNameSize);
    }
    const savedCustomerNameSize = localStorage.getItem(customerNameSizeKey);
    if (savedCustomerNameSize === "large" || savedCustomerNameSize === "small") {
      setCustomerNameSizeSetting(savedCustomerNameSize);
    }
    const savedPaymentModeOnBill = localStorage.getItem(paymentModeOnBillKey);
    if (savedPaymentModeOnBill !== null) {
      setShowPaymentModeOnBillSetting(savedPaymentModeOnBill === "true");
    }
    setShowLogoOnBillSetting(readStorageBool(printLogoKey, true));
    setShowQrOnBillSetting(readStorageBool(printUpiQrKey, true));
    setAlwaysPrintUpiQrSetting(readStorageBool(alwaysPrintUpiQrKey, false));
    setPrintTokenNoSetting(readStorageBool(printTokenKey, true));
    setPrintGoogleReviewQrSetting(readStorageBool(printReviewQrKey, false));
    setLogoSizeSetting(readStorageString(logoSizeKey, "medium"));
    setUpiQrSizeSetting(readStorageString(upiQrSizeKey, "large"));
    setGoogleReviewQrSizeSetting(readStorageString(reviewQrSizeKey, "small"));
    setGstPrintFormatSetting(readStorageString(gstFormatKey, "both"));
    setBusinessNameSizeSetting(readStorageString(businessNameSizeKey, "medium"));
    setTotalAmountSizeSetting(readStorageString(totalAmountSizeKey, "medium"));
    setSavingAmountSizeSetting(readStorageString(savingAmountSizeKey, "medium"));
    const stored = JSON.parse(localStorage.getItem("userData") || "{}");
    const u = stored?.data || stored || {};
    setShowPhoneOnBillSetting(String(u?.showphonenumberonprinter ?? "1") === "1");
    setShowThanksOnBillSetting(String(u?.showthanksonbill ?? "1") === "1");
    const onStorage = (event) => {
      if (event.key === printerWidthKey || event.key === printerWidthLegacyKey) {
        const next = normalizePrinterWidthPreference(event.newValue) || "2in";
        setPrinterWidthSetting(next);
        return;
      }
      if (event.key === taxBreakupKey) {
        setShowTaxBreakupOnBillSetting(event.newValue === "true");
        return;
      }
      if (event.key === restaurantNameSizeKey) {
        const next = event.newValue;
        if (next === "large" || next === "small") {
          setRestaurantNameSizeSetting(next);
        }
        return;
      }
      if (event.key === customerNameSizeKey) {
        const next = event.newValue;
        if (next === "large" || next === "small") {
          setCustomerNameSizeSetting(next);
        }
        return;
      }
      if (event.key === paymentModeOnBillKey) {
        setShowPaymentModeOnBillSetting(event.newValue === "true");
        return;
      }
      if (event.key === printLogoKey) {
        setShowLogoOnBillSetting(event.newValue === "true");
        return;
      }
      if (event.key === printUpiQrKey) {
        setShowQrOnBillSetting(event.newValue === "true");
        return;
      }
      if (event.key === alwaysPrintUpiQrKey) {
        setAlwaysPrintUpiQrSetting(event.newValue === "true");
        return;
      }
      if (event.key === printTokenKey) {
        setPrintTokenNoSetting(event.newValue === "true");
        return;
      }
      if (event.key === printReviewQrKey) {
        setPrintGoogleReviewQrSetting(event.newValue === "true");
        return;
      }
      if (event.key === logoSizeKey) {
        setLogoSizeSetting(event.newValue || "medium");
        return;
      }
      if (event.key === upiQrSizeKey) {
        setUpiQrSizeSetting(event.newValue || "large");
        return;
      }
      if (event.key === reviewQrSizeKey) {
        setGoogleReviewQrSizeSetting(event.newValue || "small");
        return;
      }
      if (event.key === gstFormatKey) {
        setGstPrintFormatSetting(event.newValue || "both");
        return;
      }
      if (event.key === businessNameSizeKey) {
        setBusinessNameSizeSetting(event.newValue || "medium");
        return;
      }
      if (event.key === totalAmountSizeKey) {
        setTotalAmountSizeSetting(event.newValue || "medium");
        return;
      }
      if (event.key === savingAmountSizeKey) {
        setSavingAmountSizeSetting(event.newValue || "medium");
        return;
      }
      if (event.key === "userData") {
        try {
          const parsed = JSON.parse(event.newValue || "{}");
          const data = parsed?.data || parsed || {};
          setShowPhoneOnBillSetting(String(data?.showphonenumberonprinter ?? "1") === "1");
          setShowThanksOnBillSetting(String(data?.showthanksonbill ?? "1") === "1");
        } catch {
          // ignore parse errors
        }
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  useEffect(() => {
    const loadOptions = async () => {
      try {
        const restId = userData?.id || initialOrder?.restaurantid || order?.restaurantid;
        if (!restId) return;

        const [variantRes, addonRes] = await Promise.all([
          fetch("https://qr.billsarthi.in/public/api/product/variants/list", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ restaurantid: restId }),
          }),
          fetch("https://qr.billsarthi.in/public/api/product/addons/list", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ restaurantid: restId }),
          }),
        ]);

        const variantJson = await variantRes.json().catch(() => ({}));
        const addonJson = await addonRes.json().catch(() => ({}));

        if (variantRes.ok && (variantJson?.success || Array.isArray(variantJson?.variants))) {
          const byId = {};
          const countByProduct = {};
          (variantJson.variants || []).forEach((variant) => {
            const id = Number(variant?.id);
            const productId = Number(variant?.productid);
            if (id) byId[id] = variant;
            if (productId) countByProduct[productId] = (countByProduct[productId] || 0) + 1;
          });
          setVariantMap(byId);
          setVariantCountByProduct(countByProduct);
        }

        if (addonRes.ok && (addonJson?.success || Array.isArray(addonJson?.addons))) {
          const byId = {};
          (addonJson.addons || []).forEach((addon) => {
            const id = Number(addon?.id);
            if (id) byId[id] = addon;
          });
          setAddonsById(byId);
        }
      } catch (e) {
        console.log("printscreen options load failed", e?.message || e);
      }
    };
    loadOptions();
  }, [userData?.id, initialOrder, order]);

  useEffect(() => {
    setEditBillNo(String(order?.ordercode || order?.code || order?.billno || ""));
    setEditBillDate(formatDateInputValue(order?.created_at));
    setEditBillTime(formatTimeInputValue(order?.created_at));
    setWhatsAppNumber(
      digitsOnly(
        order?.customermobile ||
          order?.customer_mobile ||
          order?.mobile ||
          order?.phone ||
          order?.party_mobile ||
          order?.partymobile ||
          ""
      )
    );
  }, [order]);

  useEffect(() => {
    if (!resolvedRestaurantId) return;
    const fetchEntitlement = async () => {
      try {
        const res = await fetch(`${API_BASE}/qr/entitlement-status`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ restaurantid: resolvedRestaurantId }),
        });
        const json = await res.json().catch(() => ({}));
        if (res.ok) {
          setQrEntitlement(json || {});
        }
      } catch (error) {
        console.log("print entitlement load failed", error?.message || error);
      }
    };
    fetchEntitlement();
  }, [resolvedRestaurantId]);

  // Load table metadata for labels (match mobile)
  useEffect(() => {
    const loadTables = async () => {
      try {
        const cached = localStorage.getItem("tablelistx");
        if (cached) {
          const parsed = JSON.parse(cached);
          if (Array.isArray(parsed)) setTableMetaMap(buildTableMetaMap(parsed));
        }
      } catch {}
      const restId = userData?.id || initialOrder?.restaurantid || order?.restaurantid;
      if (!restId) return;
      try {
        const res = await fetch(
          "https://qr.billsarthi.in/public/api/list/table",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ restaurantid: restId }),
          }
        );
        const json = await res.json();
        if (res.ok && Array.isArray(json?.list)) {
          setTableMetaMap(buildTableMetaMap(json.list));
          localStorage.setItem("tablelistx", JSON.stringify(json.list));
        }
      } catch (e) {
        console.log("printscreen table list load failed", e?.message || e);
      }
    };
    loadTables();
  }, [userData?.id, initialOrder, order]);

  const resolveTableLabel = useCallback(
    (raw) => {
      const value = raw === undefined || raw === null ? "" : String(raw).trim();
      if (!value || value === "0" || /take/i.test(value)) return "TA";
      const meta = tableMetaMap[value];
      return meta?.displayName || value;
    },
    [tableMetaMap]
  );

  const resolveTablePrefix = useCallback(
    (raw) => {
      const value = raw === undefined || raw === null ? "" : String(raw).trim();
      if (!value || value === "0" || /take/i.test(value)) return "Takeaway";
      const meta = tableMetaMap[value];
      return meta?.type === "room" ? "Room" : "Table";
    },
    [tableMetaMap]
  );

  /* ===== Order detail + items (getorderdetail) ===== */
  useEffect(() => {
    if (initialOrder) {
      const fromStateItems =
        (Array.isArray(initialOrder.itemslist) &&
          initialOrder.itemslist.length > 0 &&
          initialOrder.itemslist) ||
        (Array.isArray(initialOrder.orderitems) &&
          initialOrder.orderitems.length > 0 &&
          initialOrder.orderitems) ||
        (Array.isArray(initialOrder.items) && initialOrder.items.length > 0
          ? initialOrder.items
          : []);

      if (fromStateItems.length > 0) {
        setItems(fromStateItems);
        setLoading(false);
        return;
      }
    }

    const fetchDetail = async () => {
      try {
        setLoading(true);
        setError("");

        const stored = localStorage.getItem("userData");
        if (!stored) {
          setError("Login expired. Please login again.");
          return;
        }
        const parsed = JSON.parse(stored);
        const restId = parsed?.data?.id || parsed?.id;
        if (!restId) {
          setError("Restaurant not found. Please login again.");
          return;
        }

        const body = {
          restaurantid: restId,
          orderid: orderId || initialOrder?.id,
        };

        const res = await fetch(
          "https://qr.billsarthi.in/public/api/getorderdetail",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
          }
        );
        const json = await res.json();

        if (!res.ok) {
          throw new Error(json?.message || "Unable to load order details");
        }

        setOrderDetailResponse(json);
        const apiOrder = json?.orderdetails || json?.order || {};
        const merged = { ...(initialOrder || {}), ...apiOrder };
        setOrder(merged);

        const itemsFromApi = Array.isArray(json?.itemslist)
          ? json.itemslist
          : Array.isArray(merged.orderitems)
          ? merged.orderitems
          : Array.isArray(merged.items)
          ? merged.items
          : [];

        setItems(itemsFromApi);
      } catch (e) {
        console.error("getorderdetail error:", e);
        setError(e.message || "Failed to load order details");
      } finally {
        setLoading(false);
      }
    };

    fetchDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  const hasOrder = !!order;

  /* ===== Meta (Bill no, etc) ===== */
  const meta = useMemo(() => {
    if (!order)
      return {
        code: "-",
        table: "-",
        createdAt: null,
        discount: 0,
        discountType: "",
        discountAmount: 0,
        discountPercent: 0,
        total: 0,
      };

    const sourceNameRaw =
      order.table_name ??
      order.tablename ??
      order.table ??
      order.tableid ??
      order.table_no ??
      "";
    const sourceName = String(sourceNameRaw || "").trim();
    const metaEntry =
      tableMetaMap[String(order?.tableid)] ||
      tableMetaMap[String(order?.table)] ||
      tableMetaMap[String(order?.table_name)] ||
      tableMetaMap[String(order?.tablename)] ||
      tableMetaMap[sourceName];

    const typeFromOrder = String(
      order?.table_type ||
        order?.tableType ||
        order?.tabletype ||
        order?.type ||
        ""
    ).toLowerCase();

    const type =
      metaEntry?.type ||
      (typeFromOrder.includes("room")
        ? "room"
        : typeFromOrder.includes("table")
        ? "table"
        : sourceName.toLowerCase().includes("room")
        ? "room"
        : "table");

    const isTakeaway =
      !sourceName ||
      sourceName === "0" ||
      sourceName === "-1" ||
      Number(sourceName) === 0 ||
      Number(sourceName) === -1 ||
      isTakeawayName(sourceName);

    const displayCandidate =
      metaEntry?.displayName ||
      order?.table_label ||
      order?.table_name ||
      order?.tablename ||
      order?.table ||
      sourceName ||
      "-";

    let tableLabel;
    let tablePrefix = "Table";
    if (isTakeaway) {
      tableLabel = formatTakeawayLabel(sourceName || "TakeAway-1");
      tablePrefix = "Takeaway";
    } else if (type === "room") {
      tablePrefix = "Room";
      tableLabel = displayCandidate.toLowerCase().startsWith("room")
        ? displayCandidate
        : `Room ${displayCandidate}`;
    } else {
      tablePrefix = "Table";
      tableLabel = displayCandidate.toLowerCase().startsWith("table")
        ? displayCandidate
        : `Table ${displayCandidate}`;
    }

    // DEBUG: log how table label is resolved (remove after verification)
    console.log("PrintScreen table label debug", {
      orderId: order?.id || order?.ordercode,
      tableid: order?.tableid,
      table: order?.table,
      table_name: order?.table_name,
      tablename: order?.tablename,
      table_type: order?.table_type || order?.tableType || order?.tabletype,
      resolvedLabel: tableLabel,
      isTakeaway,
      tablePrefix,
    });

    return {
      code: order.ordercode || order.code || order.billno || order.id,
      table: tableLabel,
      tablePrefix,
      createdAt: order.created_at,
      discount: N(order.discount),
      discountType: (order.discount_type || "").toLowerCase(),
      discountAmount: N(order.discount_amount),
      discountPercent: N(order.discount_percent),
      total: N(order.totalamount),
    };
  }, [order, tableMetaMap]);

  const gstDetails = useMemo(
    () =>
      orderDetailResponse?.gstdetails ||
      order?.gstdetails ||
      order?.gstDetails ||
      null,
    [orderDetailResponse, order]
  );

  const variantCountFallback = useMemo(() => {
    const counts = {};
    Object.values(variantMap || {}).forEach((variant) => {
      const productId = Number(variant?.productid);
      if (!productId) return;
      counts[productId] = (counts[productId] || 0) + 1;
    });
    return counts;
  }, [variantMap]);

  const displayItems = useMemo(() => {
    return (items || []).map((it, index) => {
      const productId = Number(it?.productid ?? it?.id ?? 0);
      const qty = N(it?.qty ?? it?.quantity ?? 0);
      const price = N(it?.price ?? 0);
      const priceaftertax = N(
        it?.priceaftertax ?? it?.price_after_tax ?? it?.rate_with_tax ?? it?.price ?? 0
      );
      const taxedprice = N(it?.taxedprice ?? it?.priceaftertax ?? it?.price ?? 0);
      const rate = priceaftertax || taxedprice || price;
      const addonTotal = N(it?.addon_total ?? 0);
      const variantId = it?.variantid ? Number(it?.variantid) : null;
      const addonIds = parseAddonIds(
        it?.addons_json ?? it?.addon_ids ?? it?.addons
      );
      const addonLabel = addonIds
        .map((id) => addonsById[id]?.name)
        .filter(Boolean)
        .join(", ");
      const baseName =
        it?.product_name || it?.productname || it?.name || "Item";
      const variantMeta = variantId ? variantMap[variantId] : null;
      const variantLabel = String(
        variantMeta?.label ||
          variantMeta?.name ||
          (it?.variant_label ? String(it.variant_label) : "") ||
          (it?.variantname ? String(it.variantname) : "") ||
          (it?.variant_name ? String(it.variant_name) : "") ||
          (it?.size ? String(it.size) : "") ||
          ""
      ).trim();
      const variantCount =
        Number.isFinite(variantCountByProduct[productId]) ?
          variantCountByProduct[productId] :
          variantCountFallback[productId];
      const isDefaultVariant =
        variantMeta?.is_default === 1 || variantMeta?.is_default === "1";
      const shouldShowVariant =
        variantLabel &&
        (Number.isFinite(variantCount) ? variantCount > 1 : !isDefaultVariant);
      const hasVariantSuffix =
        shouldShowVariant &&
        variantLabel &&
        String(baseName).includes(`(${variantLabel})`);
      const name = shouldShowVariant
        ? hasVariantSuffix
          ? baseName
          : `${baseName} (${variantLabel})`
        : baseName;
      const fullName = addonLabel ? `${name} + ${addonLabel}` : name;
      const lineKey = buildLineKey(productId, variantId, addonIds);

      return {
        ...it,
        id: it?.id ?? lineKey ?? `${productId}-${index}`,
        lineKey,
        productid: productId,
        qty,
        price,
        priceaftertax,
        taxedprice,
        rate,
        amount: qty * rate,
        variantid: variantId,
        addon_ids: addonIds,
        addon_label: addonLabel,
        variant_label: variantLabel,
        addon_total: addonTotal,
        base_name: baseName,
        name,
        full_name: fullName,
      };
    });
  }, [items, addonsById, variantMap, variantCountByProduct, variantCountFallback]);

  const printProfile = useMemo(
    () => PRINT_PROFILES[printerWidthSetting] || PRINT_PROFILES["2in"],
    [printerWidthSetting]
  );
  const paperMeta = useMemo(
    () => getPaperMeta(printerWidthSetting),
    [printerWidthSetting]
  );

  /* ===== GST + Discount + Total (items based) ===== */
  const calc = useMemo(() => {
    if (!order)
      return {
        breakup: [],
        totalGST: 0,
        discAmt: 0,
        final: 0,
        taxable: 0,
        gross: 0,
        taxLabel: "GST",
      };

    const genericTaxDetails = parseMaybeJson(
      orderDetailResponse?.tax_details ||
        orderDetailResponse?.taxDetails ||
        order?.tax_details ||
        order?.taxDetails ||
        null
    );
    const genericBreakup = normalizeTaxDetailRows(genericTaxDetails);
    const breakup = genericBreakup.length
      ? genericBreakup
      : parseMaybeJson(gstDetails)?.breakup ||
        orderDetailResponse?.gstbreakup ||
        orderDetailResponse?.gst_breakup ||
        order?.gstbreakup ||
        order?.gst_breakup ||
        [];
    const extracted = extractGSTDetails(gstDetails, breakup, displayItems);
    const totalGST = N(genericTaxDetails?.tax_total) || extracted.totalGST;

    let gross = 0;
    for (const it of displayItems || []) {
      const qty = N(it?.qty ?? it?.quantity ?? it?.qtyy);
      const rate = N(
        it?.priceaftertax ??
          it?.price_after_tax ??
          it?.rate_with_tax ??
          it?.price ??
          0
      );
      gross += qty * rate;
    }

    const { amount: discAmt, taxable } = computeDiscount({
      total: meta.total,
      discount: meta.discount,
      discount_type: meta.discountType,
      discount_amount: meta.discountAmount,
      discount_percent: meta.discountPercent,
      gstTotal: totalGST,
    });

    const final = meta.total || Math.max(0, gross + totalGST - discAmt);

    return {
      breakup: extracted.rows,
      totalGST,
      discAmt,
      final,
      taxable,
      gross,
      taxLabel: genericTaxDetails?.tax_label || "GST",
    };
  }, [order, orderDetailResponse, gstDetails, displayItems, meta]);

  /* ===== Totals section ===== */
  const totals = useMemo(() => {
    if (!order)
      return {
        subtotal: 0,
        gst: 0,
        discount: 0,
        grand: 0,
      };

    const subtotal = calc.gross ?? 0;
    const discount = calc.discAmt ?? 0;
    const grand = calc.final ?? 0;
    const deliveryChargeValue = Number(order?.delivery_charge ?? 0) || 0;
    const inferredGst = Math.max(
      0,
      Number((grand - subtotal - deliveryChargeValue + discount).toFixed(2))
    );
    const gst = calc.totalGST > 0 ? calc.totalGST : inferredGst;
    return { subtotal, gst, discount, grand };
  }, [order, calc]);
  const customerDisplay = useMemo(
    () =>
      String(order?.customername || order?.partyname || order?.customer || "CASH").trim(),
    [order]
  );
  const deliveryCharge = useMemo(
    () => Number(order?.delivery_charge ?? 0) || 0,
    [order]
  );
  const deliveryEnabled = useMemo(
    () => Number(order?.delivery_enabled ?? 0) === 1 || deliveryCharge > 0,
    [order, deliveryCharge]
  );
  const displayTaxLabel = useMemo(
    () => String(calc?.taxLabel || "GST").trim() || "GST",
    [calc]
  );
  const invoiceFileName = useMemo(
    () => `invoice_${String(meta.code || orderId || "order").replace(/[^a-z0-9_-]+/gi, "_")}.pdf`,
    [meta.code, orderId]
  );
  const whatsAppSummaryText = useMemo(() => {
    const lines = [
      `${restInfo.restaurant_name || "Restaurant"} Invoice`,
      `Bill No: ${meta.code || "-"}`,
      `${meta.tablePrefix || "Table"}: ${meta.table || "-"}`,
      `Date: ${formatDateTime(meta.createdAt)}`,
      `Customer: ${customerDisplay || "CASH"}`,
      `Total: ₹${money(totals.grand)}`,
      "",
      "Invoice PDF is ready to share.",
    ];
    return lines.join("\n");
  }, [
    restInfo.restaurant_name,
    meta.code,
    meta.tablePrefix,
    meta.table,
    meta.createdAt,
    customerDisplay,
    totals.grand,
  ]);

  /* ===== GST breakup rows ===== */
  const taxRows = useMemo(() => {
    if (!calc.breakup || !calc.breakup.length) {
      const rowsFromItems = (displayItems || [])
        .map((item) => {
          const qty = N(item?.qty ?? item?.quantity ?? item?.qtyy ?? 0);
          if (qty <= 0) return null;
          const taxable = N(
            item?.priceaftertax ??
              item?.price_after_tax ??
              item?.rate_with_tax ??
              item?.price ??
              0
          ) * qty;
          const cgst = N(item?.cgst ?? item?.cgstAmount ?? 0) * qty;
          const sgst = N(item?.sgst ?? item?.sgstAmount ?? 0) * qty;
          const igst = N(item?.igst ?? item?.igstAmount ?? 0) * qty;
          const total = N(item?.taxamount ?? item?.tax_amount ?? 0) * qty;
          const finalTotal = igst > 0 ? igst : total || cgst + sgst;
          if (finalTotal <= 0) return null;
          return {
            taxable,
            rate: 0,
            cgst: igst > 0 ? 0 : cgst,
            sgst: igst > 0 ? 0 : sgst,
            igst,
            total: finalTotal,
          };
        })
        .filter(Boolean);
      if (!rowsFromItems.length) {
        return { rows: [], splitTaxBreakup: true };
      }
      const printable = normalizePrintableTaxBreakup(rowsFromItems, displayTaxLabel);
      return {
        splitTaxBreakup: printable.splitTaxBreakup,
        rows: printable.rows.map((row) => ({
          taxable: N(row?.taxableamount ?? row?.taxable ?? row?.taxableAmount),
          rate: N(row?.rate ?? row?.taxRate ?? row?.percent ?? row?.percentage),
          cgst: N(row?.cgst ?? row?.cgstAmount ?? row?.cgstvalue ?? 0),
          sgst: N(row?.sgst ?? row?.sgstAmount ?? row?.sgstvalue ?? 0),
          igst: N(row?.igst ?? row?.igstAmount ?? 0),
          total: N(row?.total ?? row?.totalamount ?? 0),
        })),
      };
    }
    const printable = normalizePrintableTaxBreakup(calc.breakup, displayTaxLabel);
    return {
      splitTaxBreakup: printable.splitTaxBreakup,
      rows: printable.rows
      .map((row) => {
        const taxable = N(
          row?.taxableamount ?? row?.taxable ?? row?.taxableAmount
        );
        const igst = N(row?.igst ?? row?.igstAmount ?? 0);
        const cgst = igst
          ? 0
          : N(row?.cgst ?? row?.cgstAmount ?? row?.cgstvalue ?? 0);
        const sgst = igst
          ? 0
          : N(row?.sgst ?? row?.sgstAmount ?? row?.sgstvalue ?? 0);
        const totalVal =
          igst > 0 ? igst : cgst + sgst || N(row?.total ?? row?.totalamount);
        return {
          taxable,
          rate: N(row?.rate ?? row?.taxRate ?? row?.percent ?? row?.percentage),
          cgst,
          sgst,
          igst,
          total: totalVal,
        };
      })
      .filter((r) => r.total > 0),
    };
  }, [calc.breakup, displayItems, displayTaxLabel]);

  const knownServiceIds = useMemo(
    () => [
      "0000ffe0-0000-1000-8000-00805f9b34fb",
      "0000ffe1-0000-1000-8000-00805f9b34fb",
      "0000ff00-0000-1000-8000-00805f9b34fb",
      "0000ff01-0000-1000-8000-00805f9b34fb",
      "0000ff02-0000-1000-8000-00805f9b34fb",
      "000018f0-0000-1000-8000-00805f9b34fb",
      "000018f1-0000-1000-8000-00805f9b34fb",
    ],
    []
  );
  const knownCharIds = useMemo(
    () => [
      "0000ffe1-0000-1000-8000-00805f9b34fb",
      "0000ff02-0000-1000-8000-00805f9b34fb",
      "0000ff01-0000-1000-8000-00805f9b34fb",
    ],
    []
  );
  const rememberBleDevice = useCallback((device) => {
    try {
      const id = device?.id || "";
      if (id) {
        lastBleDeviceIdRef.current = id;
        localStorage.setItem("ble_last_device_id", id);
        localStorage.setItem("printerConnectedId", id);
      }
      localStorage.setItem(
        "printerConnected",
        String(device?.name || id || "Connected Printer")
      );
    } catch {
      // ignore storage errors
    }
  }, []);
  const resolveCharacteristicFromDevice = useCallback(
    async (device) => {
      if (!device?.gatt) return null;
      if (!device.gatt.connected) {
        await device.gatt.connect();
      }
      const server = device.gatt;
      const services = await server.getPrimaryServices();
      const orderedServices = [
        ...services.filter((svc) =>
          knownServiceIds.includes(String(svc.uuid || "").toLowerCase())
        ),
        ...services.filter(
          (svc) => !knownServiceIds.includes(String(svc.uuid || "").toLowerCase())
        ),
      ];
      for (const svc of orderedServices) {
        const chars = await svc.getCharacteristics();
        const orderedChars = [
          ...chars.filter((c) =>
            knownCharIds.includes(String(c.uuid || "").toLowerCase())
          ),
          ...chars.filter(
            (c) => !knownCharIds.includes(String(c.uuid || "").toLowerCase())
          ),
        ];
        for (const c of orderedChars) {
          const { write, writeWithoutResponse } = c.properties || {};
          if (write || writeWithoutResponse) {
            printerCharRef.current = c;
            rememberBleDevice(device);
            setBleStatus(`Connected: ${device.name || device.id}`);
            return c;
          }
        }
      }
      return null;
    },
    [knownServiceIds, knownCharIds, rememberBleDevice]
  );

  const connectBlePrinter = useCallback(async ({ allowPrompt = true } = {}) => {
    if (!navigator?.bluetooth) {
      throw new Error("Web Bluetooth not supported in this browser.");
    }
    try {
      const devices = navigator.bluetooth.getDevices
        ? await navigator.bluetooth.getDevices()
        : [];
      const lastId = lastBleDeviceIdRef.current;
      const ordered = lastId
        ? [
            ...devices.filter((d) => d.id === lastId),
            ...devices.filter((d) => d.id !== lastId),
          ]
        : devices;
      for (const device of ordered) {
        try {
          const c = await resolveCharacteristicFromDevice(device);
          if (c) return c;
        } catch {
          // try next paired device
        }
      }
    } catch {
      // continue with prompt flow
    }

    if (!allowPrompt) {
      throw new Error("No paired BLE printer found");
    }
    setBleStatus("Scanning for printers…");
    const device = await navigator.bluetooth.requestDevice({
      acceptAllDevices: true,
      optionalServices: [
        0xffe0,
        0xffe5,
        0x18f0,
        0x18f1,
        0xff00,
        0xff01,
        0xff02,
        "0000ffe0-0000-1000-8000-00805f9b34fb",
        "0000ffe1-0000-1000-8000-00805f9b34fb",
        "0000ff00-0000-1000-8000-00805f9b34fb",
        "0000ff01-0000-1000-8000-00805f9b34fb",
        "0000ff02-0000-1000-8000-00805f9b34fb",
        "000018f0-0000-1000-8000-00805f9b34fb",
        "000018f1-0000-1000-8000-00805f9b34fb",
      ],
    });

    const connected = await resolveCharacteristicFromDevice(device);
    if (connected) return connected;
    throw new Error("No writable characteristic found. Printer might be classic Bluetooth.");
  }, [resolveCharacteristicFromDevice]);

  const writeBle = useCallback(async (bytes) => {
    const char = printerCharRef.current;
    if (!char) throw new Error("Printer not connected");
    const parts = chunkBytes(bytes, 20);
    const canWriteWithResponse = !!char.writeValue;
    const canWriteWithoutResponse = !!char.writeValueWithoutResponse;
    const shouldUseFastMode =
      canWriteWithoutResponse && (parts.length > 80 || !canWriteWithResponse);

    const sendWithAck = async () => {
      if (!canWriteWithResponse) throw new Error("Write-with-response not available");
      for (let i = 0; i < parts.length; i += 1) {
        await char.writeValue(parts[i]);
        await new Promise((r) => setTimeout(r, 1));
      }
    };

    const sendFast = async () => {
      if (!canWriteWithoutResponse) throw new Error("Fast write not available");
      for (let i = 0; i < parts.length; i += 1) {
        await char.writeValueWithoutResponse(parts[i]);
        await new Promise((r) => setTimeout(r, 3));
        if ((i + 1) % 30 === 0) {
          await new Promise((r) => setTimeout(r, 18));
        }
      }
    };

    if (shouldUseFastMode) {
      try {
        await sendFast();
      } catch (fastErr) {
        if (canWriteWithResponse) {
          await sendWithAck();
        } else {
          throw fastErr;
        }
      }
    } else if (canWriteWithResponse) {
      await sendWithAck();
    } else if (canWriteWithoutResponse) {
      await sendFast();
    } else {
      throw new Error("Selected printer characteristic is not writable.");
    }
  }, []);

  const tryAutoReconnect = useCallback(async () => {
    if (!navigator?.bluetooth?.getDevices) return false;
    const lastId = lastBleDeviceIdRef.current;
    try {
      const devices = await navigator.bluetooth.getDevices();
      if (!devices?.length) return false;
      const ordered = lastId
        ? [
            ...devices.filter((d) => d.id === lastId),
            ...devices.filter((d) => d.id !== lastId),
          ]
        : devices;
      for (const device of ordered) {
        try {
          const c = await resolveCharacteristicFromDevice(device);
          if (c) return true;
        } catch {
          // try next device
        }
      }
      return false;
    } catch {
      return false;
    }
  }, [resolveCharacteristicFromDevice]);

  useEffect(() => {
    tryAutoReconnect();
  }, [tryAutoReconnect]);


  /* ===== QR (UPI ya generic) ===== */
  const logoUrl = useMemo(() => {
    const rel = restInfo?.avatar_url || "";
    if (!rel) return "";
    return /^https?:\/\//i.test(rel)
      ? rel
      : `https://qr.billsarthi.com/public/${rel}`;
  }, [restInfo]);

  const showPhoneOnBill = showPhoneOnBillSetting && !!restInfo?.mobile_number;
  const showQRCodeOnBill = !!(restInfo?.upiaddress || "").trim();
  const paymentModeLabel = String(order?.paymentmode || order?.mode || "").toLowerCase();
  const shouldPrintUpiQr =
    showQrOnBillSetting &&
    showQRCodeOnBill &&
    (alwaysPrintUpiQrSetting || paymentModeLabel === "upi");
  const gstEnabled = gstPrintFormatSetting !== "none";
  const gstVisible = Number(totals.gst || 0) > 0;
  const printProfileWithSizes = useMemo(
    () => ({
      ...printProfile,
      logoWidth: sizeToLogoPixels(logoSizeSetting, printProfile.logoWidth || 320),
      qrSize: sizeToQrPixels(upiQrSizeSetting, printProfile.qrSize || 220),
    }),
    [printProfile, logoSizeSetting, upiQrSizeSetting]
  );
  const restaurantNameSizeForPrint =
    businessNameSizeSetting === "small" || businessNameSizeSetting === "large"
      ? businessNameSizeSetting
      : restaurantNameSizeSetting;

  const qrInfo = useMemo(() => {
    const upiId = (restInfo?.upiaddress || "").trim();
    if (upiId && shouldPrintUpiQr) {
      const amountStr = Number(N(calc.final) || 0).toFixed(2);
      const payee = (restInfo?.restaurant_name || "Restaurant").slice(0, 25);
      const note = `Bill ${meta.code || ""}`.trim();

      const upiUrl =
        `upi://pay?pa=${encodeURIComponent(upiId)}` +
        `&pn=${encodeURIComponent(payee)}` +
        `&am=${encodeURIComponent(amountStr)}` +
        `&cu=INR` +
        `&tn=${encodeURIComponent(note)}`;

      const qrImg = `https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(
        upiUrl
      )}`;

      return {
        type: "upi",
        qrImg,
        label: "Scan & Pay",
        upiId,
        amountStr,
      };
    }

    if (printGoogleReviewQrSetting) {
      const reviewUrl = (restInfo?.google_review_url || "").trim();
      if (reviewUrl) {
        const qrImg = `https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(
          reviewUrl
        )}`;
        return {
          type: "review",
          qrImg,
          label: "Scan to review us",
        };
      }
    }
    return null;
  }, [
    restInfo,
    calc.final,
    meta.code,
    shouldPrintUpiQr,
    printGoogleReviewQrSetting,
  ]);
  const handleBlePrint = useCallback(async () => {
    if (!order) return;
    setBlePrinting(true);
    try {
      if (printerCharRef.current) {
        const activeDevice = printerCharRef.current?.service?.device;
        if (activeDevice?.gatt && !activeDevice.gatt.connected) {
          printerCharRef.current = null;
        }
      }
      if (!printerCharRef.current) {
        const autoConnected = await Promise.race([
          tryAutoReconnect(),
          new Promise((resolve) => setTimeout(() => resolve(false), 2200)),
        ]);
        if (!autoConnected || !printerCharRef.current) {
          await connectBlePrinter();
        }
      }

      const lineWidth = printProfileWithSizes.lineWidth || BLE_LINE_WIDTH;
      const nameW = printProfileWithSizes.nameW || NAME_W;
      const qtyW = printProfileWithSizes.qtyW || QTY_W;
      const rateW = printProfileWithSizes.rateW || RATE_W;
      const amtW = printProfileWithSizes.amtW || AMT_W;
      const taxCols =
        printProfileWithSizes.taxCols || PRINT_PROFILES["2in"].taxCols;

      const receiptItems = displayItems
        .filter((it) => Number(it?.qty || 0) > 0)
        .map((it) => ({
          name: it?.full_name || it?.name || "Item",
          qty: Number(it?.qty || 0),
          rate: Number(it?.rate || 0),
          total: Number(it?.amount || 0),
        }));

      const customerLabel =
        order?.customername || order?.partyname || order?.customer || "";
      const paymentMode = order?.paymentmode || order?.mode || "";
      const lines = [];
      lines.push(
        ...buildSizedCenteredLines(
          restInfo.restaurant_name,
          lineWidth,
          restaurantNameSizeForPrint
        )
      );
      [restInfo.addressline1, restInfo.addressline2, restInfo.addressline3]
        .filter(Boolean)
        .forEach((ln) => lines.push(centerLine(ln, lineWidth)));
      if (showPhoneOnBillSetting && restInfo.mobile_number)
        lines.push(centerLine(`Phone: ${restInfo.mobile_number}`, lineWidth));
      if (restInfo.gstno) lines.push(centerLine(`GSTIN: ${restInfo.gstno}`, lineWidth));
      lines.push(
        pairLine(
          "Bill",
          meta.code,
          "Date",
          formatDateTime(meta.createdAt),
          lineWidth
        )
      );
      lines.push(
        pairLine(
          meta.tablePrefix || "Table",
          meta.table,
          showPaymentModeOnBillSetting ? "Payment" : "",
          showPaymentModeOnBillSetting ? paymentMode || "N/A" : "",
          lineWidth
        )
      );
      if (customerLabel) {
        lines.push(pairLine("Customer", customerLabel, "", "", lineWidth));
      }
      lines.push("-".repeat(lineWidth));
      lines.push(col4("ITEM", "Q", "RATE", "AMT", { nameW, qtyW, rateW, amtW }));
      lines.push("-".repeat(lineWidth));
      receiptItems.forEach((it) => {
        const nameParts = wrapText(it.name, nameW);
        const rateStr = Number(it.rate || 0).toFixed(2);
        const amtStr = Number(it.total || 0).toFixed(2);
        if (nameParts.length) {
          lines.push(col4(nameParts[0], it.qty, rateStr, amtStr, { nameW, qtyW, rateW, amtW }));
          nameParts.slice(1).forEach((part) => {
            lines.push(col4(part, "", "", "", { nameW, qtyW, rateW, amtW }));
          });
        }
      });
      lines.push("-".repeat(lineWidth));
      lines.push(padLine("Subtotal", money(totals.subtotal), lineWidth));
      if (gstVisible) {
        lines.push(padLine(displayTaxLabel, money(totals.gst), lineWidth));
      }
      if (totals.discount > 0)
        lines.push(padLine("Discount", `-${money(totals.discount)}`, lineWidth));
      if (deliveryEnabled && deliveryCharge > 0)
        lines.push(padLine("Delivery", money(deliveryCharge), lineWidth));
      lines.push(padLine("Grand Total", money(totals.grand), lineWidth));

      if ((gstEnabled || gstVisible) && showTaxBreakupOnBillSetting && taxRows.rows.length) {
        lines.push("-".repeat(lineWidth));
        lines.push(centerLine("Tax Breakup", lineWidth));
        lines.push(
          taxRows.splitTaxBreakup
            ? "Taxable".padEnd(taxCols.taxable, " ") +
                "CGST".padStart(taxCols.cgst, " ") +
                "SGST".padStart(taxCols.sgst, " ") +
                "Total".padStart(taxCols.total, " ")
            : "Taxable".padEnd(taxCols.taxable, " ") +
                displayTaxLabel
                  .slice(0, Math.max(3, taxCols.total))
                  .padStart(taxCols.cgst + taxCols.sgst + taxCols.total, " ")
        );
        taxRows.rows.forEach((r) => {
          const line = taxRows.splitTaxBreakup
            ? money(r.taxable).padStart(taxCols.taxable, " ") +
              money(r.cgst).padStart(taxCols.cgst, " ") +
              money(r.sgst).padStart(taxCols.sgst, " ") +
              money(r.total).padStart(taxCols.total, " ")
            : money(r.taxable).padStart(taxCols.taxable, " ") +
              money(r.total).padStart(
                taxCols.cgst + taxCols.sgst + taxCols.total,
                " "
              );
          lines.push(line.slice(0, lineWidth));
        });
      }

      const receiptText = lines.join("\n");

      const chunks = [INIT_CMD, ALIGN_LEFT];
      const logoRasterPromise = showLogoOnBillSetting
        ? logoRasterFromUrl(logoUrl, printProfileWithSizes.logoWidth || 120)
        : Promise.resolve(null);
      if (showLogoOnBillSetting) {
        try {
          const logoCmd = await logoRasterPromise;
          if (logoCmd) {
            chunks.push(ALIGN_CENTER);
            chunks.push(logoCmd);
            chunks.push(ALIGN_LEFT);
          } else {
            console.log("Logo render skipped: all logo sources failed");
          }
        } catch (e) {
          console.log("Logo render failed", e);
        }
      }

      const enc = new TextEncoder();
      chunks.push(enc.encode(receiptText + "\n"));

      const upi = (restInfo?.upiaddress || "").trim();
      if (shouldPrintUpiQr && upi) {
        try {
          const qrCommands = buildEscPosQrCommands(
            `upi://pay?pa=${encodeURIComponent(upi)}`,
            { moduleSize: lineWidth >= 48 ? 7 : 5, ecc: 48 }
          );
          if (qrCommands.length) {
            chunks.push(enc.encode("\n"));
            chunks.push(ALIGN_CENTER);
            qrCommands.forEach((cmd) => chunks.push(cmd));
            chunks.push(enc.encode("\n"));
            chunks.push(ALIGN_LEFT);
          }
        } catch (e) {
          console.log("QR render failed", e);
        }
      }
      if (printGoogleReviewQrSetting && restInfo?.google_review_url) {
        try {
          const reviewQrModuleSize =
            googleReviewQrSizeSetting === "large"
              ? lineWidth >= 48
                ? 7
                : 5
              : googleReviewQrSizeSetting === "small"
              ? 4
              : lineWidth >= 48
              ? 6
              : 5;
          const reviewCommands = buildEscPosQrCommands(restInfo.google_review_url, {
            moduleSize: reviewQrModuleSize,
            ecc: 48,
          });
          if (reviewCommands.length) {
            chunks.push(enc.encode("\n"));
            chunks.push(ALIGN_CENTER);
            chunks.push(enc.encode("Google Review\n"));
            reviewCommands.forEach((cmd) => chunks.push(cmd));
            chunks.push(enc.encode("\n"));
            chunks.push(ALIGN_LEFT);
          }
        } catch (e) {
          console.log("Review QR render failed", e);
        }
      }

      if (showThanksOnBillSetting) {
        chunks.push(ALIGN_CENTER);
        chunks.push(enc.encode("\n"));
        chunks.push(enc.encode("THANKS PLEASE VISIT AGAIN\n"));
        chunks.push(ALIGN_LEFT);
      }
      chunks.push(enc.encode("\n\n\n"));
      chunks.push(CUT_CMD);

      const totalLen = chunks.reduce((s, arr) => s + arr.length, 0);
      const payload = new Uint8Array(totalLen);
      let offset = 0;
      chunks.forEach((arr) => {
        payload.set(arr, offset);
        offset += arr.length;
      });

      const sendWithTimeout = () =>
        Promise.race([
          writeBle(payload),
          new Promise((_, reject) =>
            setTimeout(() => reject(new Error("Print timed out")), 35000)
          ),
        ]);

      try {
        await sendWithTimeout();
      } catch (firstErr) {
        printerCharRef.current = null;
        const recovered = await tryAutoReconnect();
        if (!recovered || !printerCharRef.current) throw firstErr;
        await sendWithTimeout();
      }
      setBleStatus("Printed via BLE.");
    } catch (err) {
      console.error(err);
      setBleStatus(err?.message || "Bluetooth print failed");
    } finally {
      setBlePrinting(false);
    }
  }, [
    order,
    connectBlePrinter,
    tryAutoReconnect,
    displayItems,
    restInfo,
    restaurantNameSizeForPrint,
    customerNameSizeSetting,
    showLogoOnBillSetting,
    showQrOnBillSetting,
    showPhoneOnBillSetting,
    showThanksOnBillSetting,
    showPaymentModeOnBillSetting,
    deliveryCharge,
    deliveryEnabled,
    meta.code,
    meta.table,
    meta.tablePrefix,
    meta.createdAt,
    printProfileWithSizes,
    totals,
    taxRows,
    displayTaxLabel,
    gstVisible,
    showTaxBreakupOnBillSetting,
    gstEnabled,
    shouldPrintUpiQr,
    printGoogleReviewQrSetting,
    googleReviewQrSizeSetting,
    logoUrl,
    writeBle,
  ]);

  const updateStoredUserData = useCallback((nextData) => {
    try {
      const raw = localStorage.getItem("userData");
      const parsed = raw ? JSON.parse(raw) : {};
      const updatedPayload = parsed?.data
        ? { ...parsed, data: nextData }
        : { ...parsed, ...nextData };
      localStorage.setItem("userData", JSON.stringify(updatedPayload));
    } catch {
      localStorage.setItem("userData", JSON.stringify({ data: nextData }));
    }
  }, []);

  const handleLogoFileChange = useCallback((event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setPickedLogoFile(file);
    const previewUrl = URL.createObjectURL(file);
    setPickedLogoPreview((current) => {
      if (current) URL.revokeObjectURL(current);
      return previewUrl;
    });
  }, []);

  useEffect(() => {
    return () => {
      if (pickedLogoPreview) URL.revokeObjectURL(pickedLogoPreview);
    };
  }, [pickedLogoPreview]);

  const handleUploadLogo = useCallback(async () => {
    if (!pickedLogoFile) {
      toast.info("Pick a logo first.");
      return;
    }
    if (!resolvedRestaurantId) {
      toast.error("Restaurant not found.");
      return;
    }
    setLogoUploading(true);
    try {
      const form = new FormData();
      form.append("restaurantid", String(resolvedRestaurantId));
      form.append("photo", pickedLogoFile);
      const response = await fetch(`${API_BASE}/printer/logo`, {
        method: "POST",
        body: form,
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok || !result?.data) {
        throw new Error(result?.message || "Logo upload failed");
      }
      const updated = { ...(userData || {}), ...result.data };
      updateStoredUserData(updated);
      setRestInfo((prev) => ({
        ...prev,
        avatar_url:
          result?.data?.logo ||
          result?.data?.restaurant_logo ||
          result?.data?.avatar_url ||
          result?.data?.photo ||
          prev.avatar_url,
      }));
      setPickedLogoFile(null);
      if (pickedLogoPreview) {
        URL.revokeObjectURL(pickedLogoPreview);
      }
      setPickedLogoPreview("");
      setLogoModalOpen(false);
      toast.success("Logo updated.");
    } catch (error) {
      toast.error(error?.message || "Logo upload failed");
    } finally {
      setLogoUploading(false);
    }
  }, [
    pickedLogoFile,
    pickedLogoPreview,
    resolvedRestaurantId,
    updateStoredUserData,
    userData,
  ]);

  const handleSaveUpi = useCallback(async () => {
    const value = String(upiValue || "").trim();
    if (!value) {
      toast.info("Enter UPI ID.");
      return;
    }
    if (!resolvedRestaurantId) {
      toast.error("Restaurant not found.");
      return;
    }
    setUpiSaving(true);
    try {
      const response = await fetch(`${API_BASE}/printer/upi`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          restaurantid: resolvedRestaurantId,
          upiaddress: value,
        }),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok || !result?.data) {
        throw new Error(result?.message || "Unable to save UPI");
      }
      const updated = { ...(userData || {}), ...result.data };
      updateStoredUserData(updated);
      setRestInfo((prev) => ({
        ...prev,
        upiaddress: result?.data?.upiaddress || value,
      }));
      setUpiModalOpen(false);
      toast.success("UPI saved.");
    } catch (error) {
      toast.error(error?.message || "Unable to save UPI");
    } finally {
      setUpiSaving(false);
    }
  }, [resolvedRestaurantId, upiValue, updateStoredUserData, userData]);

  const handleSaveBillMeta = useCallback(async () => {
    const billNo = String(editBillNo || "").trim();
    if (!billNo) {
      toast.info("Bill number is required.");
      return;
    }
    if (!resolvedRestaurantId || !order?.id) {
      toast.error("Unable to update bill details.");
      return;
    }
    const merged = mergeDateAndTime(editBillDate, editBillTime);
    if (!merged || merged > new Date()) {
      toast.info("Select a valid bill date and time.");
      return;
    }
    setSavingBillMeta(true);
    try {
      const response = await fetch(`${API_BASE}/order/update-meta`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          restaurantid: resolvedRestaurantId,
          orderid: order.id,
          billno: billNo,
          billdate: formatBillDateForApi(merged),
          billtime: formatBillTimeForApi(merged),
        }),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok || !result?.success) {
        throw new Error(result?.message || "Unable to update bill details");
      }
      const updatedMeta = result?.data || result?.order || result?.orderdetails || {};
      setOrder((prev) => ({
        ...(prev || {}),
        ...updatedMeta,
        ordercode: updatedMeta?.ordercode || billNo,
        billno: updatedMeta?.billno || billNo,
        created_at: updatedMeta?.created_at || merged.toISOString(),
      }));
      setOrderDetailResponse((prev) => ({
        ...(prev || {}),
        orderdetails: {
          ...(prev?.orderdetails || {}),
          ...updatedMeta,
          ordercode: updatedMeta?.ordercode || billNo,
          billno: updatedMeta?.billno || billNo,
          created_at: updatedMeta?.created_at || merged.toISOString(),
        },
      }));
      setBillMetaModalOpen(false);
      toast.success("Bill details updated.");
    } catch (error) {
      toast.error(error?.message || "Unable to update bill details");
    } finally {
      setSavingBillMeta(false);
    }
  }, [editBillDate, editBillNo, editBillTime, order, resolvedRestaurantId]);

  const handleDownloadInvoicePdf = useCallback(async () => {
    if (!receiptRef.current) {
      toast.info("Invoice preview not ready.");
      return;
    }
    try {
      await downloadPDF(receiptRef.current, invoiceFileName);
      toast.success("Invoice PDF downloaded.");
    } catch (error) {
      toast.error(error?.message || "Unable to download PDF");
    }
  }, [invoiceFileName]);

  const handleShareOnWhatsApp = useCallback(async () => {
    const digits = digitsOnly(whatsAppNumber);
    if (!digits) {
      toast.info("Enter WhatsApp number.");
      return;
    }
    setSharingInvoice(true);
    try {
      if (receiptRef.current) {
        await downloadPDF(receiptRef.current, invoiceFileName);
      }
      const url = `https://wa.me/${digits}?text=${encodeURIComponent(
        whatsAppSummaryText
      )}`;
      window.open(url, "_blank", "noopener,noreferrer");
      setWhatsAppModalOpen(false);
      toast.success("WhatsApp opened. Attach the downloaded PDF if needed.");
    } catch (error) {
      toast.error(error?.message || "Unable to prepare WhatsApp invoice");
    } finally {
      setSharingInvoice(false);
    }
  }, [invoiceFileName, whatsAppNumber, whatsAppSummaryText]);

  const handleRechargeSupport = useCallback(() => {
    openSupportWhatsApp(
      `Hi, I want to recharge QR credits.\nRestaurant ID: ${resolvedRestaurantId || "-"}\nRequested amount: ₹${Number(
        rechargeAmount || 0
      ).toFixed(0)}\nCurrent remaining QR credits: ${Number(
        qrEntitlement?.remaining ?? 0
      )}`
    );
  }, [qrEntitlement?.remaining, rechargeAmount, resolvedRestaurantId]);

  const handlePhonePeRecharge = useCallback(() => {
    navigate("/plan-selection");
  }, [navigate]);


  /* ===== UI ===== */
  return (
    <Page $paper={paperMeta}>
      <Toolbar $paper={paperMeta}>
        <ToolbarLeft>
          <ToolbarTitle>Print Preview</ToolbarTitle>
          <ToolbarSub>Bill No. {meta.code}</ToolbarSub>
        </ToolbarLeft>
        <ToolbarActions>
          <SecondaryBtn onClick={() => navigate(-1)}>Back</SecondaryBtn>
        </ToolbarActions>
      </Toolbar>
      {bleStatus ? <BleStatus>{bleStatus}</BleStatus> : null}

      <ReceiptWrapper $paper={paperMeta}>
        {loading ? (
          <ReceiptCard $paper={paperMeta}>
            <p>Loading order details…</p>
          </ReceiptCard>
        ) : error ? (
          <ReceiptCard $paper={paperMeta}>
            <ErrorTitle>Error</ErrorTitle>
            <p>{error}</p>
          </ReceiptCard>
        ) : !hasOrder ? (
          <ReceiptCard $paper={paperMeta}>
            <ErrorTitle>No order data</ErrorTitle>
            <p>Couldn&apos;t load order #{orderId}</p>
          </ReceiptCard>
        ) : (
          <ReceiptCard $paper={paperMeta} ref={receiptRef}>
            {/* HEADER: Restaurant + Bill meta */}
            <ReceiptHeader>
              <HeaderLeft>
                {showLogoOnBillSetting && !!logoUrl ? (
                  <Logo>
                    <img src={logoUrl} alt="Logo" />
                  </Logo>
                ) : null}
                <div>
                  <RestName
                    style={{
                      fontSize: `${sizeToTextRem(businessNameSizeSetting, 1.34)}rem`,
                    }}
                  >
                    {restInfo.restaurant_name}
                  </RestName>
                  {restInfo.addressline1 && (
                    <RestLine>{restInfo.addressline1}</RestLine>
                  )}
                  {restInfo.addressline2 && (
                    <RestLine>{restInfo.addressline2}</RestLine>
                  )}
                  {restInfo.addressline3 && (
                    <RestLine>{restInfo.addressline3}</RestLine>
                  )}
                  {showPhoneOnBill && (
                    <RestMeta>Call: {restInfo.mobile_number}</RestMeta>
                  )}
                  {restInfo.gstno && (
                    <RestMeta>GSTIN: {restInfo.gstno}</RestMeta>
                  )}
                </div>
              </HeaderLeft>

              <HeaderRight>
                <MetaChip>
                  <MetaLabel>Bill No</MetaLabel>
                  <MetaValue>{meta.code}</MetaValue>
                </MetaChip>
                <MetaChip>
                  <MetaLabel>Date</MetaLabel>
                  <MetaValue>{formatDateTime(meta.createdAt)}</MetaValue>
                </MetaChip>
                <MetaChip>
                  <MetaLabel>{meta.tablePrefix || "Table"}</MetaLabel>
                  <MetaValue>{meta.table}</MetaValue>
                </MetaChip>
                {showPaymentModeOnBillSetting ? (
                  <MetaChip>
                    <MetaLabel>Payment</MetaLabel>
                    <MetaValue>{order.paymentmode || "N/A"}</MetaValue>
                  </MetaChip>
                ) : null}
              </HeaderRight>
            </ReceiptHeader>

            {/* CUSTOMER / PAYMENT */}
            {customFieldRows.length > 0 || customerDisplay ? (
              <Section>
                <SectionRow>
                  {customFieldRows.map((row) => (
                    <SectionItem key={row.label}>
                      <Label>{row.label}</Label>
                      <SectionValue>{row.value}</SectionValue>
                    </SectionItem>
                  ))}
                  <SectionItem>
                    <Label>Customer</Label>
                    <SectionValue>{customerDisplay}</SectionValue>
                  </SectionItem>
                </SectionRow>
              </Section>
            ) : null}

            <Divider />

            {/* ITEMS */}
            <ItemsTable>
              <thead>
                <tr>
                  <th style={{ width: "50%" }}>Item</th>
                  <th>Qty</th>
                  <th>Rate</th>
                  <th>Amount</th>
                </tr>
              </thead>
              <tbody>
                {displayItems.map((it, idx) => {
                  const qty = N(it.qty || it.quantity || it.qtyy || 0);
                  const rate = N(
                    it.priceaftertax ??
                      it.price_after_tax ??
                      it.rate_with_tax ??
                      it.price ??
                      0
                  );
                  const name =
                    it.full_name ||
                    it.product_name ||
                    it.productname ||
                    it.name ||
                    "Item";
                  return (
                    <tr key={idx}>
                      <td>{name}</td>
                      <td>{qty}</td>
                      <td>₹{money(rate)}</td>
                      <td>₹{money(qty * rate)}</td>
                    </tr>
                  );
                })}
                {!displayItems.length && (
                  <tr>
                    <td colSpan={4} style={{ textAlign: "center" }}>
                      No items for this order
                    </td>
                  </tr>
                )}
              </tbody>
            </ItemsTable>

            {/* TOTALS + GST */}
            <BottomArea>
              <BottomLeft>
                {(gstEnabled || gstVisible) && showTaxBreakupOnBillSetting && taxRows.rows.length > 0 && (
                  <GSTBreakup>
                    <GSTTitle>{displayTaxLabel} Breakup</GSTTitle>
                    <div className="line head">
                      <span>Taxable</span>
                      <span>{taxRows.splitTaxBreakup ? "CGST" : displayTaxLabel}</span>
                      <span>{taxRows.splitTaxBreakup ? "SGST" : ""}</span>
                      <span>Total</span>
                    </div>
                    {taxRows.rows.map((r, i) => (
                      <div className="line" key={i}>
                        <span>₹{money(r.taxable)}</span>
                        <span>
                          ₹{money(taxRows.splitTaxBreakup ? r.cgst : r.total)}
                        </span>
                        <span>{taxRows.splitTaxBreakup ? `₹${money(r.sgst)}` : ""}</span>
                        <span>₹{money(r.total)}</span>
                      </div>
                    ))}
                  </GSTBreakup>
                )}

                {qrInfo && (
                  <QRRow>
                    <QRText>
                      <Label>{qrInfo.label}</Label>
                      {qrInfo.type === "upi" ? (
                        <>
                          <QRSub>UPI: {qrInfo.upiId}</QRSub>
                          <QRAmount>Amount: ₹{qrInfo.amountStr}</QRAmount>
                        </>
                      ) : (
                        <QRSub>Scan QR to see details</QRSub>
                      )}
                    </QRText>
                    <QRBox>
                      <img src={qrInfo.qrImg} alt="QR" />
                    </QRBox>
                  </QRRow>
                )}
              </BottomLeft>

              <TotalsCard>
                <TotalsRow>
                  <span>Subtotal</span>
                  <span>₹{money(totals.subtotal)}</span>
                </TotalsRow>
                {gstVisible && (
                  <TotalsRow>
                    <span>{displayTaxLabel}</span>
                    <span>₹{money(totals.gst)}</span>
                  </TotalsRow>
                )}
                {totals.discount > 0 && (
                  <TotalsRow>
                    <span>Discount</span>
                    <span
                      style={{
                        fontSize: `${sizeToTextRem(savingAmountSizeSetting, 1)}rem`,
                      }}
                    >
                      -₹{money(totals.discount)}
                    </span>
                  </TotalsRow>
                )}
                {deliveryEnabled && deliveryCharge > 0 && (
                  <TotalsRow>
                    <span>Delivery</span>
                    <span>₹{money(deliveryCharge)}</span>
                  </TotalsRow>
                )}
                <TotalsDivider />
                <TotalsRow bold>
                  <span>Grand Total</span>
                  <span
                    style={{
                      fontSize: `${sizeToTextRem(totalAmountSizeSetting, 1.14)}rem`,
                    }}
                  >
                    ₹{money(totals.grand)}
                  </span>
                </TotalsRow>
              </TotalsCard>
            </BottomArea>

            {showThanksOnBillSetting ? (
              <FooterNote>Thank you for dining with us!</FooterNote>
            ) : null}
          </ReceiptCard>
        )}

      </ReceiptWrapper>

      {!loading && hasOrder ? (
        <FooterActionsWrap>
          <FooterGroup>
            <FooterGroupTitle>Bill Actions</FooterGroupTitle>
            <FooterButtonRow>
              <SecondaryBtn onClick={() => setBillMetaModalOpen(true)}>
                Edit Bill Details
              </SecondaryBtn>
              <SecondaryBtn onClick={handleDownloadInvoicePdf}>
                Download PDF
              </SecondaryBtn>
              <SecondaryBtn onClick={() => setWhatsAppModalOpen(true)}>
                WhatsApp Invoice
              </SecondaryBtn>
            </FooterButtonRow>
          </FooterGroup>

          <FooterGroup>
            <FooterGroupTitle>Print Actions</FooterGroupTitle>
            <FooterButtonRow>
              <PrimaryBtn onClick={() => window.print()}>Print</PrimaryBtn>
              <SecondaryBtn onClick={handleBlePrint} disabled={blePrinting}>
                {blePrinting ? "Printing…" : "Thermal Print (BLE)"}
              </SecondaryBtn>
            </FooterButtonRow>
          </FooterGroup>

          <FooterGroup>
            <FooterGroupTitle>Setup</FooterGroupTitle>
            <FooterMetaLine>
              <span>Logo: {restInfo.avatar_url ? "Added" : "Not Added"}</span>
              <span>UPI: {restInfo.upiaddress ? "Ready" : "Missing"}</span>
            </FooterMetaLine>
            <FooterButtonRow>
              <SecondaryBtn onClick={() => setLogoModalOpen(true)}>Upload Logo</SecondaryBtn>
              <SecondaryBtn onClick={() => setUpiModalOpen(true)}>Edit UPI</SecondaryBtn>
            </FooterButtonRow>
          </FooterGroup>

          <FooterGroup>
            <FooterGroupTitle>QR Credits</FooterGroupTitle>
            <FooterMetaLine>
              <span>Remaining: {Number(qrEntitlement?.remaining ?? 0)}</span>
              <span>Auto Add: {qrEntitlement?.can_auto_add ? "Yes" : "No"}</span>
            </FooterMetaLine>
            <RechargeChips>
              {DEFAULT_RECHARGE_AMOUNTS.map((amount) => (
                <RechargeChip
                  key={amount}
                  type="button"
                  $active={Number(rechargeAmount) === amount}
                  onClick={() => setRechargeAmount(amount)}
                >
                  ₹{amount}
                </RechargeChip>
              ))}
            </RechargeChips>
            <FooterButtonRow>
              <SecondaryBtn onClick={handleRechargeSupport}>WhatsApp Recharge</SecondaryBtn>
              <PrimaryBtn onClick={handlePhonePeRecharge}>PhonePe Recharge</PrimaryBtn>
            </FooterButtonRow>
          </FooterGroup>
        </FooterActionsWrap>
      ) : null}

      <ModalOverlay
        $open={logoModalOpen}
        onClick={() => !logoUploading && setLogoModalOpen(false)}
      >
        <ModalCard onClick={(e) => e.stopPropagation()}>
          <ModalTitle>Upload Logo</ModalTitle>
          <ModalSub>Upload a square logo to show on printed bills.</ModalSub>
          <FilePickerWrap>
            <input type="file" accept="image/*" onChange={handleLogoFileChange} />
          </FilePickerWrap>
          <LogoPreviewBox>
            {pickedLogoPreview || restInfo.avatar_url ? (
              <img src={pickedLogoPreview || logoUrl} alt="Logo preview" />
            ) : (
              <PreviewEmpty>No logo selected</PreviewEmpty>
            )}
          </LogoPreviewBox>
          <ModalActions>
            <SecondaryBtn onClick={() => setLogoModalOpen(false)} disabled={logoUploading}>
              Cancel
            </SecondaryBtn>
            <PrimaryBtn onClick={handleUploadLogo} disabled={logoUploading}>
              {logoUploading ? "Uploading…" : "Save Logo"}
            </PrimaryBtn>
          </ModalActions>
        </ModalCard>
      </ModalOverlay>

      <ModalOverlay
        $open={upiModalOpen}
        onClick={() => !upiSaving && setUpiModalOpen(false)}
      >
        <ModalCard onClick={(e) => e.stopPropagation()}>
          <ModalTitle>Edit UPI</ModalTitle>
          <ModalSub>Update the UPI ID used on printed bills and QR.</ModalSub>
          <ModalField>
            <ModalLabel>UPI ID</ModalLabel>
            <ModalInput
              value={upiValue}
              onChange={(e) => setUpiValue(e.target.value)}
              placeholder="name@bank"
            />
          </ModalField>
          <ModalActions>
            <SecondaryBtn onClick={() => setUpiModalOpen(false)} disabled={upiSaving}>
              Cancel
            </SecondaryBtn>
            <PrimaryBtn onClick={handleSaveUpi} disabled={upiSaving}>
              {upiSaving ? "Saving…" : "Save UPI"}
            </PrimaryBtn>
          </ModalActions>
        </ModalCard>
      </ModalOverlay>

      <ModalOverlay
        $open={billMetaModalOpen}
        onClick={() => !savingBillMeta && setBillMetaModalOpen(false)}
      >
        <ModalCard onClick={(e) => e.stopPropagation()}>
          <ModalTitle>Edit Bill Details</ModalTitle>
          <ModalSub>Update bill number, date or time for this order.</ModalSub>
          <ModalGrid>
            <ModalField>
              <ModalLabel>Bill Number</ModalLabel>
              <ModalInput
                value={editBillNo}
                onChange={(e) => setEditBillNo(e.target.value)}
                placeholder="Bill no"
              />
            </ModalField>
            <ModalField>
              <ModalLabel>Bill Date</ModalLabel>
              <ModalInput
                type="date"
                value={editBillDate}
                onChange={(e) => setEditBillDate(e.target.value)}
              />
            </ModalField>
            <ModalField>
              <ModalLabel>Bill Time</ModalLabel>
              <ModalInput
                type="time"
                value={editBillTime}
                onChange={(e) => setEditBillTime(e.target.value)}
              />
            </ModalField>
          </ModalGrid>
          <ModalActions>
            <SecondaryBtn onClick={() => setBillMetaModalOpen(false)} disabled={savingBillMeta}>
              Cancel
            </SecondaryBtn>
            <PrimaryBtn onClick={handleSaveBillMeta} disabled={savingBillMeta}>
              {savingBillMeta ? "Saving…" : "Save Bill"}
            </PrimaryBtn>
          </ModalActions>
        </ModalCard>
      </ModalOverlay>

      <ModalOverlay
        $open={whatsAppModalOpen}
        onClick={() => !sharingInvoice && setWhatsAppModalOpen(false)}
      >
        <ModalCard onClick={(e) => e.stopPropagation()}>
          <ModalTitle>WhatsApp Invoice</ModalTitle>
          <ModalSub>
            PDF download hoga, phir WhatsApp chat open hogi. PDF attach karke customer ko send kar sakte ho.
          </ModalSub>
          <ModalField>
            <ModalLabel>WhatsApp Number</ModalLabel>
            <ModalInput
              value={whatsAppNumber}
              onChange={(e) => setWhatsAppNumber(digitsOnly(e.target.value))}
              placeholder="91XXXXXXXXXX"
            />
          </ModalField>
          <MessagePreview>{whatsAppSummaryText}</MessagePreview>
          <ModalActions>
            <SecondaryBtn onClick={() => setWhatsAppModalOpen(false)} disabled={sharingInvoice}>
              Cancel
            </SecondaryBtn>
            <PrimaryBtn onClick={handleShareOnWhatsApp} disabled={sharingInvoice}>
              {sharingInvoice ? "Preparing…" : "Download + Open WhatsApp"}
            </PrimaryBtn>
          </ModalActions>
        </ModalCard>
      </ModalOverlay>
    </Page>
  );
};

export default PrintScreen;

/* ===== Styled Components (WOW + Print friendly) ===== */

const Page = styled.div`
  min-height: 100vh;
  background:
    linear-gradient(180deg, #eef2f6 0%, #e9edf3 100%);
  padding: 22px 16px 34px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI",
    sans-serif;

  @media print {
    background: #ffffff !important;
    padding: 0;
    @page {
      size: ${({ $paper }) => $paper?.printSize || "58mm auto"};
      margin: ${({ $paper }) => $paper?.printMargin || "4mm"};
    }
  }
`;

const Toolbar = styled.div`
  width: 100%;
  max-width: ${({ $paper }) => $paper?.screenWidth || "820px"};
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  margin-bottom: 14px;
  border-radius: 12px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  box-shadow: 0 4px 14px rgba(15, 23, 42, 0.06);
  padding: 12px 14px;

  @media print {
    display: none;
  }
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const ToolbarLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const ToolbarTitle = styled.div`
  text-transform: none;
  letter-spacing: 0.01em;
  font-size: 1rem;
  font-weight: 800;
  color: #0f172a;
`;

const ToolbarSub = styled.small`
  color: #015b9a;
  font-weight: 700;
`;

const ToolbarActions = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;
const BleStatus = styled.div`
  margin: 0 0 12px;
  width: 100%;
  max-width: ${({ $paper }) => $paper?.screenWidth || "820px"};
  color: #1e293b;
  font-size: 0.86rem;
  font-weight: 700;
  border: 1px solid #bfdbfe;
  background: #eff6ff;
  border-radius: 10px;
  padding: 8px 11px;
`;

const FooterActionsWrap = styled.div`
  width: 100%;
  max-width: ${({ $paper }) => $paper?.screenWidth || "820px"};
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 20px;
  margin-bottom: 12px;

  @media print {
    display: none;
  }
`;

const FooterGroup = styled.div`
  background: #ffffff;
  border: 1px solid #dbe5ef;
  border-radius: 14px;
  padding: 14px 16px;
  box-shadow: 0 6px 18px rgba(15, 23, 42, 0.05);
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const FooterGroupTitle = styled.div`
  color: #0f172a;
  font-size: 0.82rem;
  font-weight: 900;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const CreditMetaRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
`;

const CreditMetaBox = styled.div`
  border: 1px solid #dbe5ef;
  border-radius: 10px;
  padding: 10px;
  background: #f8fbff;
`;

const CreditValue = styled.div`
  margin-top: 3px;
  color: #0f172a;
  font-size: 1rem;
  font-weight: 900;
`;

const RechargeChips = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const FooterButtonRow = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;

  > button {
    flex: 1 1 180px;
  }

  @media (max-width: 640px) {
    > button {
      flex-basis: 100%;
    }
  }
`;

const FooterMetaLine = styled.div`
  display: flex;
  gap: 14px;
  flex-wrap: wrap;
  color: #64748b;
  font-size: 0.8rem;
  font-weight: 700;
`;

const RechargeChip = styled.button`
  border-radius: 999px;
  border: 1px solid ${({ $active }) => ($active ? "#015b9a" : "#cbd5e1")};
  background: ${({ $active }) => ($active ? "#015b9a" : "#ffffff")};
  color: ${({ $active }) => ($active ? "#ffffff" : "#0f172a")};
  font-size: 0.76rem;
  font-weight: 800;
  padding: 7px 11px;
  cursor: pointer;
`;

const ReceiptWrapper = styled.div`
  width: 100%;
  max-width: ${({ $paper }) =>
    $paper?.key === "a4"
      ? "720px"
      : $paper?.key === "4in"
      ? "520px"
      : $paper?.key === "3in"
      ? "460px"
      : "400px"};

  @media print {
    max-width: ${({ $paper }) => $paper?.printWidth || "58mm"};
  }
`;

const ReceiptCard = styled.div`
  background: #ffffff;
  border-radius: 0;
  padding: ${({ $paper }) => ($paper?.key === "a4" ? "18px 20px 20px" : "14px 14px 18px")};
  box-shadow: 0 18px 40px rgba(15, 23, 42, 0.12);
  position: relative;
  overflow: hidden;
  border: 1px solid #d8dde6;

  &::before {
    content: "";
    position: absolute;
    left: 8px;
    right: 8px;
    top: 0;
    height: 10px;
    background:
      radial-gradient(circle at 10px 5px, transparent 5px, #eef2f6 5.5px) left top / 20px 10px repeat-x;
    pointer-events: none;
  }

  &::after {
    content: "";
    position: absolute;
    left: 8px;
    right: 8px;
    bottom: 0;
    height: 10px;
    background:
      radial-gradient(circle at 10px 5px, transparent 5px, #eef2f6 5.5px) left bottom / 20px 10px repeat-x;
    pointer-events: none;
  }

  > * {
    position: relative;
    z-index: 1;
  }

  @media print {
    box-shadow: none;
    border-radius: 0;
    border: none;
    padding: 10px;
    max-width: ${({ $paper }) => $paper?.printWidth || "58mm"};
    &::before {
      display: none;
    }
    &::after {
      display: none;
    }
  }
`;

const TopStrip = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: 8px;
  margin-bottom: 14px;
`;

const TopStripLabel = styled.div`
  padding: 3px 14px;
  border-radius: 999px;
  border: 1px solid #0f172a;
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #0f172a;
`;

const ReceiptHeader = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 10px;
  align-items: flex-start;
  padding-bottom: 10px;
  border-bottom: 1px dashed #cbd5e1;
`;

const HeaderLeft = styled.div`
  display: flex;
  gap: 10px;
  align-items: flex-start;
  min-width: 0;
`;

const HeaderRight = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 6px;
  align-items: stretch;
  width: 100%;
  @media (max-width: 768px) {
    width: 100%;
    grid-template-columns: 1fr 1fr;
  }
`;

const Logo = styled.div`
  width: 58px;
  height: 58px;
  border-radius: 0;
  background: #ffffff;
  border: 1px solid #d7dbe2;
  display: grid;
  place-items: center;
  padding: 5px;
  overflow: hidden;
  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
  @media print {
    width: 52px;
    height: 52px;
    border-radius: 0;
    box-shadow: none;
    border-color: #d4d8df;
    padding: 4px;
  }
`;

const RestName = styled.h1`
  margin: 0;
  font-size: 0.98rem;
  font-weight: 900;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #0f172a;
`;

const RestLine = styled.p`
  margin: 0;
  font-size: 0.78rem;
  color: #334155;
  line-height: 1.25;
`;

const RestMeta = styled.p`
  margin: 2px 0 0 0;
  font-size: 0.76rem;
  color: #0f172a;
  font-weight: 700;
`;

const MetaChip = styled.div`
  border-radius: 0;
  border: 1px solid #d6dbe3;
  padding: 5px 7px;
  min-width: 0;
  background: #ffffff;

  @media (max-width: 768px) {
    min-width: 0;
  }
`;

const MetaLabel = styled.div`
  font-size: 0.66rem;
  text-transform: uppercase;
  letter-spacing: 0.16em;
  color: #64748b;
  font-weight: 800;
`;

const MetaValue = styled.div`
  font-size: 0.74rem;
  font-weight: 900;
  color: #0f172a;
`;

const Section = styled.div`
  margin-top: 10px;
`;

const SectionRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 6px;
  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const SectionItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  border: 1px solid #dde2ea;
  border-radius: 0;
  background: #ffffff;
  padding: 6px 8px;
`;

const SectionValue = styled.div`
  font-size: 0.76rem;
  font-weight: 800;
  color: #111827;
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px dashed #cbd5e1;
  margin: 12px 0;
`;

const ItemsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.8rem;
  margin-top: 2px;
  th,
  td {
    padding: 5px 4px;
    border-bottom: 1px dotted #d7dde7;
  }
  th {
    text-transform: uppercase;
    font-size: 0.68rem;
    letter-spacing: 0.14em;
    color: #4b5563;
    font-weight: 900;
  }
  td {
    color: #111827;
  }
  td:nth-child(2),
  th:nth-child(2) {
    width: 60px;
    text-align: center;
  }
  td:nth-child(3),
  td:nth-child(4),
  th:nth-child(3),
  th:nth-child(4) {
    text-align: right;
    width: 110px;
    font-variant-numeric: tabular-nums;
  }

  @media (max-width: 768px) {
    font-size: 0.76rem;
    th,
    td {
      white-space: nowrap;
    }
  }
`;

const BottomArea = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
  margin-top: 12px;
`;

const BottomLeft = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const GSTBreakup = styled.div`
  background: #ffffff;
  border-radius: 0;
  border: 1px solid #dde2ea;
  padding: 8px;
  font-size: 0.72rem;
  .line {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    font-weight: 700;
    color: #0f172a;
    padding: 1px 0;
  }
  .line.head {
    color: #64748b;
    text-transform: uppercase;
    font-size: 0.62rem;
    letter-spacing: 0.12em;
  }
`;

const GSTTitle = styled.div`
  font-size: 0.72rem;
  font-weight: 900;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #0f172a;
  margin-bottom: 4px;
`;

const QRRow = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: end;
  gap: 10px;
`;

const QRText = styled.div`
  min-width: 0;

  @media (max-width: 640px) {
    grid-column: 1 / -1;
  }
`;

const QRSub = styled.p`
  margin: 0;
  color: #334155;
  font-size: 0.76rem;
`;

const QRAmount = styled.p`
  margin: 0;
  color: #0f172a;
  font-weight: 900;
  font-size: 0.8rem;
`;

const QRBox = styled.div`
  width: 120px;
  height: 120px;
  border-radius: 0;
  border: 1px solid #d8dde6;
  background: #ffffff;
  display: grid;
  place-items: center;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;

const TotalsCard = styled.div`
  border-radius: 0;
  border: 1px solid #d8dde6;
  padding: 8px 10px;
  background: #ffffff;
`;

const TotalsRow = styled.div`
  display: flex;
  justify-content: space-between;
  font-weight: ${({ bold }) => (bold ? 900 : 700)};
  color: ${({ bold }) => (bold ? "#0f172a" : "#111827")};
  font-size: ${({ bold }) => (bold ? "0.98rem" : "0.78rem")};
  margin: 2px 0;
`;

const TotalsDivider = styled.div`
  border-top: 1px dashed #cfd6df;
  margin: 6px 0;
`;

const FooterNote = styled.div`
  margin-top: 16px;
  text-align: center;
  color: #475569;
  font-weight: 800;
  font-size: 0.8rem;
  letter-spacing: 0.16em;
  text-transform: uppercase;
`;

const Label = styled.span`
  font-size: 0.66rem;
  font-weight: 900;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.14em;
`;

const PrimaryBtn = styled.button`
  background: linear-gradient(180deg, #0f77bc 0%, #015b9a 100%);
  color: #fff;
  border: 1px solid #015b9a;
  padding: 10px 16px;
  border-radius: 999px;
  font-weight: 800;
  font-size: 0.84rem;
  cursor: pointer;
  letter-spacing: 0.02em;
  text-transform: none;
  box-shadow: 0 8px 16px rgba(1, 91, 154, 0.22);
  transition: transform 0.12s ease, filter 0.2s ease;
  &:hover {
    filter: brightness(1.05);
    transform: translateY(-1px);
  }
  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
    transform: none;
    filter: none;
  }
`;

const SecondaryBtn = styled.button`
  background: #ffffff;
  color: #015b9a;
  border: 1px solid #015b9a;
  padding: 10px 16px;
  border-radius: 999px;
  font-weight: 800;
  font-size: 0.82rem;
  cursor: pointer;
  letter-spacing: 0.02em;
  text-transform: none;
  transition: transform 0.12s ease, background 0.2s ease;
  &:hover {
    background: rgba(1, 91, 154, 0.08);
    transform: translateY(-1px);
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const ErrorTitle = styled.h2`
  margin: 0 0 4px 0;
  font-size: 1rem;
  color: #b91c1c;
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.45);
  display: ${({ $open }) => ($open ? "grid" : "none")};
  place-items: center;
  padding: 16px;
  z-index: 50;
`;

const ModalCard = styled.div`
  width: min(100%, 520px);
  background: #ffffff;
  border-radius: 16px;
  border: 1px solid #dbe5ef;
  box-shadow: 0 20px 45px rgba(15, 23, 42, 0.18);
  padding: 18px;
`;

const ModalTitle = styled.h3`
  margin: 0;
  color: #0f172a;
  font-size: 1rem;
  font-weight: 900;
`;

const ModalSub = styled.p`
  margin: 6px 0 0;
  color: #64748b;
  font-size: 0.84rem;
  line-height: 1.5;
`;

const ModalGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-top: 14px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const ModalField = styled.label`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 14px;
`;

const ModalLabel = styled.span`
  color: #334155;
  font-size: 0.76rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.06em;
`;

const ModalInput = styled.input`
  width: 100%;
  box-sizing: border-box;
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  padding: 11px 12px;
  font-size: 0.9rem;
  color: #0f172a;
  background: #ffffff;
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  flex-wrap: wrap;
  margin-top: 18px;
`;

const FilePickerWrap = styled.div`
  margin-top: 14px;
  input {
    width: 100%;
    border: 1px dashed #94a3b8;
    border-radius: 10px;
    padding: 12px;
    background: #f8fbff;
  }
`;

const LogoPreviewBox = styled.div`
  margin-top: 14px;
  height: 170px;
  border: 1px solid #dbe5ef;
  border-radius: 14px;
  background: #f8fbff;
  display: grid;
  place-items: center;
  overflow: hidden;

  img {
    max-width: 85%;
    max-height: 85%;
    object-fit: contain;
  }
`;

const PreviewEmpty = styled.div`
  color: #94a3b8;
  font-size: 0.86rem;
  font-weight: 700;
`;

const MessagePreview = styled.pre`
  margin: 14px 0 0;
  padding: 12px;
  border-radius: 12px;
  background: #f8fbff;
  border: 1px solid #dbe5ef;
  color: #0f172a;
  font-size: 0.8rem;
  line-height: 1.5;
  white-space: pre-wrap;
  font-family: inherit;
`;
